import isArray from 'lodash/isArray';
import reduce from 'lodash/reduce';
import { sanitizeEntity } from './sanitize';
import nandi from '../assets/images/nadiAirport.jpeg';
import coral from '../assets/images/coralCoastImage.jpeg';
import sunCoast from '../assets/images/sunCoastmage.jpeg';
import pacific from '../assets/images/pacificHarbourImage.png';
import savusavu from '../assets/images/savusavuImageFinal.png';
import taveuni from '../assets/images/taveuniImage.png';
import kadavu from '../assets/images/kadavu.jpg';
import lau from '../assets/images/lauImage.png';
import rotu from '../assets/images/rotumaImage.png';
import Yasawa from '../assets/images/YasawaImage.jpeg';
import Mamanuca from '../assets/images/MamanucaIslandsImage.jpeg';
import HighlandsImage from '../assets/images/HighlandsImage.jpeg';
import OvalauImage from '../assets/images/OvalauImage.jpeg';

// NOTE: This file imports sanitize.js, which may lead to circular dependency

/**
 * Combine the given relationships objects
 *
 * See: http://jsonapi.org/format/#document-resource-object-relationships
 */
export const combinedRelationships = (oldRels, newRels) => {
  if (!oldRels && !newRels) {
    // Special case to avoid adding an empty relationships object when
    // none of the resource objects had any relationships.
    return null;
  }
  return { ...oldRels, ...newRels };
};

/**
 * Combine the given resource objects
 *
 * See: http://jsonapi.org/format/#document-resource-objects
 */
export const combinedResourceObjects = (oldRes, newRes) => {
  const { id, type } = oldRes;
  if (newRes.id.uuid !== id.uuid || newRes.type !== type) {
    throw new Error('Cannot merge resource objects with different ids or types');
  }
  const attributes = newRes.attributes || oldRes.attributes;
  const attributesOld = oldRes.attributes || {};
  const attributesNew = newRes.attributes || {};
  // Allow (potentially) sparse attributes to update only relevant fields
  const attrs = attributes ? { attributes: { ...attributesOld, ...attributesNew } } : null;
  const relationships = combinedRelationships(oldRes.relationships, newRes.relationships);
  const rels = relationships ? { relationships } : null;
  return { id, type, ...attrs, ...rels };
};

/**
 * Combine the resource objects form the given api response to the
 * existing entities.
 */
export const updatedEntities = (oldEntities, apiResponse, sanitizeConfig = {}) => {
  const { data, included = [] } = apiResponse;
  const objects = (Array.isArray(data) ? data : [data]).concat(included);

  const newEntities = objects.reduce((entities, curr) => {
    const { id, type } = curr;

    // Some entities (e.g. listing and user) might include extended data,
    // you should check if src/util/sanitize.js needs to be updated.
    const current = sanitizeEntity(curr, sanitizeConfig);

    entities[type] = entities[type] || {};
    const entity = entities[type][id.uuid];
    entities[type][id.uuid] = entity ? combinedResourceObjects({ ...entity }, current) : current;

    return entities;
  }, oldEntities);

  return newEntities;
};

/**
 * Denormalise the entities with the resources from the entities object
 *
 * This function calculates the dernormalised tree structure from the
 * normalised entities object with all the relationships joined in.
 *
 * @param {Object} entities entities object in the SDK Redux store
 * @param {Array<{ id, type }} resources array of objects
 * with id and type
 * @param {Boolean} throwIfNotFound wheather to skip a resource that
 * is not found (false), or to throw an Error (true)
 *
 * @return {Array} the given resource objects denormalised that were
 * found in the entities
 */
export const denormalisedEntities = (entities, resources, throwIfNotFound = true) => {
  const denormalised = resources.map(res => {
    const { id, type } = res;
    const entityFound = entities[type] && id && entities[type][id.uuid];
    if (!entityFound) {
      if (throwIfNotFound) {
        throw new Error(`Entity with type "${type}" and id "${id ? id.uuid : id}" not found`);
      }
      return null;
    }
    const entity = entities[type][id.uuid];
    const { relationships, ...entityData } = entity;

    if (relationships) {
      // Recursively join in all the relationship entities
      return reduce(
        relationships,
        (ent, relRef, relName) => {
          // A relationship reference can be either a single object or
          // an array of objects. We want to keep that form in the final
          // result.
          const hasMultipleRefs = Array.isArray(relRef.data);
          const multipleRefsEmpty = hasMultipleRefs && relRef.data.length === 0;
          if (!relRef.data || multipleRefsEmpty) {
            ent[relName] = hasMultipleRefs ? [] : null;
          } else {
            const refs = hasMultipleRefs ? relRef.data : [relRef.data];

            // If a relationship is not found, an Error should be thrown
            const rels = denormalisedEntities(entities, refs, true);

            ent[relName] = hasMultipleRefs ? rels : rels[0];
          }
          return ent;
        },
        entityData
      );
    }
    return entityData;
  });
  return denormalised.filter(e => !!e);
};

/**
 * Denormalise the data from the given SDK response
 *
 * @param {Object} sdkResponse response object from an SDK call
 *
 * @return {Array} entities in the response with relationships
 * denormalised from the included data
 */
export const denormalisedResponseEntities = sdkResponse => {
  const apiResponse = sdkResponse.data;
  const data = apiResponse.data;
  const resources = Array.isArray(data) ? data : [data];

  if (!data || resources.length === 0) {
    return [];
  }

  const entities = updatedEntities({}, apiResponse);
  return denormalisedEntities(entities, resources);
};

/**
 * Denormalize JSON object.
 * NOTE: Currently, this only handles denormalization of image references
 *
 * @param {JSON} data from Asset API (e.g. page asset)
 * @param {JSON} included array of asset references (currently only images supported)
 * @returns deep copy of data with images denormalized into it.
 */
const denormalizeJsonData = (data, included) => {
  let copy;

  // Handle strings, numbers, booleans, null
  if (data === null || typeof data !== 'object') {
    return data;
  }

  // At this point the data has typeof 'object' (aka Array or Object)
  // Array is the more specific case (of Object)
  if (data instanceof Array) {
    copy = data.map(datum => denormalizeJsonData(datum, included));
    return copy;
  }

  // Generic Objects
  if (data instanceof Object) {
    copy = {};
    Object.entries(data).forEach(([key, value]) => {
      // Handle denormalization of image reference
      const hasImageRefAsValue =
        typeof value == 'object' && value?._ref?.type === 'imageAsset' && value?._ref?.id;
      // If there is no image included,
      // the _ref might contain parameters for image resolver (Asset Delivery API resolves image URLs on the fly)
      const hasUnresolvedImageRef = typeof value == 'object' && value?._ref?.resolver === 'image';

      if (hasImageRefAsValue) {
        const foundRef = included.find(inc => inc?.id === value?._ref?.id);
        copy[key] = foundRef;
      } else if (hasUnresolvedImageRef) {
        // Don't add faulty image ref
        // Note: At the time of writing, assets can expose resolver configs,
        //       which we don't want to deal with.
      } else {
        copy[key] = denormalizeJsonData(value, included);
      }
    });
    return copy;
  }

  throw new Error("Unable to traverse data! It's not JSON.");
};

/**
 * Denormalize asset json from Asset API.
 * @param {JSON} assetJson in format: { data, included }
 * @returns deep copy of asset data with images denormalized into it.
 */
export const denormalizeAssetData = assetJson => {
  const { data, included } = assetJson || {};
  return denormalizeJsonData(data, included);
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} transaction entity object, which is to be ensured against null values
 */
export const ensureTransaction = (transaction, booking = null, listing = null, provider = null) => {
  const empty = {
    id: null,
    type: 'transaction',
    attributes: {},
    booking,
    listing,
    provider,
  };
  return { ...empty, ...transaction };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} booking entity object, which is to be ensured against null values
 */
export const ensureBooking = booking => {
  const empty = { id: null, type: 'booking', attributes: {} };
  return { ...empty, ...booking };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} listing entity object, which is to be ensured against null values
 */
export const ensureListing = listing => {
  const empty = {
    id: null,
    type: 'listing',
    attributes: { publicData: {} },
    images: [],
  };
  return { ...empty, ...listing };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} listing entity object, which is to be ensured against null values
 */
export const ensureOwnListing = listing => {
  const empty = {
    id: null,
    type: 'ownListing',
    attributes: { publicData: {} },
    images: [],
  };
  return { ...empty, ...listing };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} user entity object, which is to be ensured against null values
 */
export const ensureUser = user => {
  const empty = { id: null, type: 'user', attributes: { profile: {} } };
  return { ...empty, ...user };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} current user entity object, which is to be ensured against null values
 */
export const ensureCurrentUser = user => {
  const empty = { id: null, type: 'currentUser', attributes: { profile: {} }, profileImage: {} };
  return { ...empty, ...user };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} time slot entity object, which is to be ensured against null values
 */
export const ensureTimeSlot = timeSlot => {
  const empty = { id: null, type: 'timeSlot', attributes: {} };
  return { ...empty, ...timeSlot };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} availability exception entity object, which is to be ensured against null values
 */
export const ensureDayAvailabilityPlan = availabilityPlan => {
  const empty = { type: 'availability-plan/day', entries: [] };
  return { ...empty, ...availabilityPlan };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} availability exception entity object, which is to be ensured against null values
 */
export const ensureAvailabilityException = availabilityException => {
  const empty = { id: null, type: 'availabilityException', attributes: {} };
  return { ...empty, ...availabilityException };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} stripeCustomer entity from API, which is to be ensured against null values
 */
export const ensureStripeCustomer = stripeCustomer => {
  const empty = { id: null, type: 'stripeCustomer', attributes: {} };
  return { ...empty, ...stripeCustomer };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} stripeCustomer entity from API, which is to be ensured against null values
 */
export const ensurePaymentMethodCard = stripePaymentMethod => {
  const empty = {
    id: null,
    type: 'stripePaymentMethod',
    attributes: { type: 'stripe-payment-method/card', card: {} },
  };
  const cardPaymentMethod = { ...empty, ...stripePaymentMethod };

  if (cardPaymentMethod.attributes.type !== 'stripe-payment-method/card') {
    throw new Error(`'ensurePaymentMethodCard' got payment method with wrong type.
      'stripe-payment-method/card' was expected, received ${cardPaymentMethod.attributes.type}`);
  }

  return cardPaymentMethod;
};

/**
 * Get the display name of the given user as string. This function handles
 * missing data (e.g. when the user object is still being downloaded),
 * fully loaded users, as well as banned users.
 *
 * For banned or deleted users, a translated name should be provided.
 *
 * @param {propTypes.user} user
 * @param {String} defaultUserDisplayName
 *
 * @return {String} display name that can be rendered in the UI
 */
export const userDisplayNameAsString = (user, defaultUserDisplayName) => {
  const hasDisplayName = user?.attributes?.profile?.displayName;

  if (hasDisplayName) {
    return user.attributes.profile.displayName;
  } else {
    return defaultUserDisplayName || '';
  }
};

/**
 * DEPRECATED: Use userDisplayNameAsString function or UserDisplayName component instead
 *
 * @param {propTypes.user} user
 * @param {String} bannedUserDisplayName
 *
 * @return {String} display name that can be rendered in the UI
 */
export const userDisplayName = (user, bannedUserDisplayName) => {
  console.warn(
    `Function userDisplayName is deprecated!
User function userDisplayNameAsString or component UserDisplayName instead.`
  );

  return userDisplayNameAsString(user, bannedUserDisplayName);
};

/**
 * Get the abbreviated name of the given user. This function handles
 * missing data (e.g. when the user object is still being downloaded),
 * fully loaded users, as well as banned users.
 *
 * For banned  or deleted users, a default abbreviated name should be provided.
 *
 * @param {propTypes.user} user
 * @param {String} defaultUserAbbreviatedName
 *
 * @return {String} abbreviated name that can be rendered in the UI
 * (e.g. in Avatar initials)
 */
export const userAbbreviatedName = (user, defaultUserAbbreviatedName) => {
  const hasAttributes = user && user.attributes;
  const hasProfile = hasAttributes && user.attributes.profile;
  const hasDisplayName = hasProfile && user.attributes.profile.abbreviatedName;

  if (hasDisplayName) {
    return user.attributes.profile.abbreviatedName;
  } else {
    return defaultUserAbbreviatedName || '';
  }
};

/**
 * A customizer function to be used with the
 * mergeWith function from lodash.
 *
 * Works like merge in every way exept that on case of
 * an array the old value is completely overridden with
 * the new value.
 *
 * @param {Object} objValue Value of current field, denoted by key
 * @param {Object} srcValue New value
 * @param {String} key Key of the field currently being merged
 * @param {Object} object Target object that is receiving values from source
 * @param {Object} source Source object that is merged into object param
 * @param {Object} stack Tracks merged values
 *
 * @return {Object} New value for objValue if the original is an array,
 * otherwise undefined is returned, which results in mergeWith using the
 * standard merging function
 */
export const overrideArrays = (objValue, srcValue, key, object, source, stack) => {
  if (isArray(objValue)) {
    return srcValue;
  }
};

/**
 * Humanizes a line item code. Strips the "line-item/" namespace
 * definition from the beginnign, replaces dashes with spaces and
 * capitalizes the first character.
 *
 * @param {string} code a line item code
 *
 * @return {string} returns the line item code humanized
 */
export const humanizeLineItemCode = code => {
  if (!/^line-item\/.+/.test(code)) {
    throw new Error(`Invalid line item code: ${code}`);
  }
  const lowercase = code.replace(/^line-item\//, '').replace(/-/g, ' ');

  return lowercase.charAt(0).toUpperCase() + lowercase.slice(1);
};

export const generalQuestions = [
  {
    question: 'What is fijianhost.com?',
    answer:
      'fijianhost.com is a homestay platform that connects travelers with families and communities across Fiji. Instead of booking a hotel, you’ll be welcomed into a local home — where you’ll stay, share meals, and experience daily life as part of the family.',
  },
  {
    question: 'How is fijianhost.com different from other travel sites?',
    answer:
      'fijianhost.com complements the tourism industry by offering something different — a chance to stay with families in villages and rural communities, far from the usual tourist trails. While resorts offer comfort and convenience, we offer connection and belonging. Our platform is built for locals to benefit directly, ensuring that the tourism dollar reaches places it often doesn’t.',
  },
  {
    question: 'Where are the homestays located?',
    answer:
      'You’ll find fijianhost.com homestays across the country — in highland villages, coastal settlements, outer islands, and farming communities. Many are in places that don’t appear on tourist maps, but hold stories, rivers, forests, and families ready to welcome you.',
  },
  {
    question: 'Who is behind fijianhost.com?',
    answer:
      'fijianhost.com is Indigenous-owned and community-driven. We’re a team of Fijians who believe that tourism should give back — not just take. Our work is guided by respect for land, people, and the dignity of hosting.',
  },
  {
    question: 'How does fijianhost.com work?',
    answer:
      'You search for a homestay, choose one that suits you, and book directly through our platform. Your host is notified, and once confirmed, you’ll receive full details for your stay. We handle payments securely, and support is always available if you need help.',
  },
  {
    question: 'Is fijianhost.com only for international travelers?',
    answer:
      'Not at all. fijianhost.com is for anyone — whether you’re a local wanting to explore another province, a regional traveler from the Pacific, or visiting from overseas. This platform is for everyone who wants to connect with the heart of Fiji.',
  },
  {
    question: 'Is fijianhost.com safe to use?',
    answer:
      'Yes. All listings go through a basic verification process, and our team works closely with hosts to ensure quality and safety. Payments are handled securely online, and you’ll always be able to contact us if something doesn’t feel right.',
  },
];

export const fieldKeys = [
  'activities_included',
  'room_arrangements',
  'bathroom_facilities',
  'meals_kitchen',
  'power_lighting',
  'laundry_cleaning',
];
export const guestQuestions = [
  {
    question: 'What can I expect from a village homestay?',
    answer:
      'You’ll stay with a local family in their home — often a traditional bure or village-style house. Expect shared meals, genuine hospitality, simple comforts, and daily life as it happens. It’s not a hotel stay — it’s a shared way of living.',
  },
  {
    question: 'What is the sevusevu ceremony?',
    answer:
      'The sevusevu is a traditional welcome ceremony performed to show respect to the landowners and seek their blessing for your stay in the village. In most cases, your host will carry out the sevusevu on your behalf by presenting it to a village elder or chief. You’ll be part of the moment, but your host will guide and represent you throughout the process — there’s no need to worry about doing it yourself.',
  },
  {
    question: 'Is there internet or mobile service in the village?',
    answer:
      'Some villages have mobile signal and data coverage, others may have limited or no service. It’s best to check the listing details. But in many ways, the disconnection becomes part of the experience — time slows down, and presence becomes natural.',
  },
  {
    question: 'Can I drink the water?',
    answer:
      'In most cases, boiled or filtered water is provided by your host. Tap water may not always be safe to drink, especially in remote areas. Bring a reusable water bottle, and when in doubt, ask your host.',
  },
  {
    question: 'Are there cultural protocols I should know about?',
    answer:
      'Yes — village life follows customs that reflect respect and community. Your host will help guide you, but key things include dressing modestly, removing hats in certain spaces, and being mindful of your behavior around elders and sacred places.',
  },
  {
    question: 'How should I dress in the village?',
    answer:
      'Modest clothing is appreciated. For women, a sulu (sarong) and covered shoulders are ideal. For men, shorts below the knee and a t-shirt are generally fine. It’s best to avoid swimwear or revealing clothes outside of designated beach areas.',
  },
  {
    question: 'Can I take photos or videos freely?',
    answer:
      'Always ask first. People may be shy or feel uncomfortable being filmed, especially elders. It’s respectful to check before taking photos — and some places, like churches or burial grounds, may be off-limits.',
  },
  {
    question: 'What should I bring?',
    answer:
      'Bring a towel, personal toiletries, insect repellent, a flashlight or headlamp, and any medications you need. A reusable water bottle and snacks can be helpful too. If you’re joining village activities, a spare set of clothes and a sulu are useful.',
  },
  {
    question: 'Do I need travel insurance?',
    answer:
      'Yes, we strongly recommend it. Village areas may be far from clinics or hospitals, and travel insurance gives peace of mind in case of illness, injury, or emergencies.',
  },
  {
    question: 'What if I get sick or injured?',
    answer:
      'Let your host know as soon as possible. They will notify the fijianhost.com team so we can monitor the situation closely and help coordinate access to medical care or any further steps needed. In some remote areas, medical services may take time to reach you — which is why we strongly recommend having travel insurance for your protection.',
  },
  {
    question: 'What happens during a cyclone or emergency?',
    answer:
      'Hosts are trained to follow village disaster plans and will guide you to safety. The fijianhost.com team stays in close contact with the host, the village headman, and other local leaders to monitor the situation on the ground. We work together to ensure the safety and wellbeing of our guests and coordinate any necessary actions during emergencies. If you’re visiting during cyclone season, be prepared for delays or changes due to weather conditions.',
  },
  {
    question: 'Who do I contact in an emergency?',
    answer:
      'Your host should always be the first person you inform. Once you’ve told your host, they will contact the fijianhost.com team immediately. From there, we’ll work closely with your host, the village headman, and local contacts to monitor the situation and ensure your safety. You can also reach out directly to us through the support line or email listed in your booking confirmation.',
  },
  {
    question: 'How do I book a homestay on fijianhost.com?',
    answer:
      'Search for a homestay, select your dates, and submit a booking request. Once the host confirms, you’ll receive full details and a secure link to make payment. Some bookings may be instant, while others may take a short time to confirm.',
  },
  {
    question: 'How do I pay, and what’s included in the price?',
    answer:
      'You pay online through fijianhost.com using card or mobile wallet. The price includes your stay, shared meals, and basic village experiences. It also covers all applicable taxes and the platform fee (20%) which helps us operate the service and support hosts and guests. Some listings may offer add-ons like guided walks, river trips, or weaving sessions — these will be listed clearly.',
  },
  {
    question: 'Can I communicate with my host before arriving?',
    answer:
      'Yes — once your booking is confirmed, you’ll be able to message your host directly through our integrated messaging system to ask questions, confirm arrival time, or share preferences. In some cases where the host isn’t managing their listing directly, our team will let you know that you’re corresponding with a fijianhost.com support staff member. They’ll relay your message, requests, or questions to the host and respond on their behalf in consultation with them.',
  },
  {
    question: 'What if I need to cancel or change my booking?',
    answer:
      'We understand that travel plans can shift. If you cancel more than 30 days before your stay, you’ll receive a full refund. Cancellations made 15 to 29 days before check-in are eligible for a 50% refund. Unfortunately, if you cancel less than 15 days before your arrival, no refund can be issued. This policy helps protect our hosts, many of whom live in remote areas where last-minute rebookings are unlikely. Preparing for guests takes time, and each booking matters deeply to the families involved.',
  },
  {
    question: 'Do I need to bring my own bedding or towels?',
    answer:
      'Basic bedding is provided. Towels may or may not be included, so it’s always a good idea to bring your own, especially for swimming or bathing.',
  },
  {
    question: 'Are meals included? What kind of food will be served?',
    answer:
      'Yes — most stays include home-cooked meals, prepared by your host using local ingredients. You might enjoy fresh fish, root crops, fruits, and stews. If you have dietary needs, let your host know early.',
  },
  {
    question: 'Can I bring gifts for the village or host family?',
    answer:
      'You can, and it’s always appreciated — though never expected. Simple gifts like school supplies, books, or household items are thoughtful. Just check with us or your host if you’re unsure what’s appropriate.',
  },
  {
    question: 'What should I know about traveling with children or elderly guests?',
    answer:
      'Let your host know in advance. Some homes may not be suitable for those with mobility challenges, and rural travel can involve uneven paths or long drives. But many hosts will go the extra mile to make you feel at home.',
  },
  {
    question: 'Can I volunteer or help out during my stay?',
    answer:
      'You’re welcome to lend a hand if opportunities arise — whether it’s in the kitchen, garden, with crafts, or even by helping with village projects or community initiatives. But your presence alone is valued. Let things unfold naturally, and follow your host’s lead.',
  },
  {
    question: 'Are there language barriers?',
    answer:
      'Most hosts speak basic English, though conversations may be simple. Many also speak Fijian or a local dialect. You’ll find that warmth and hospitality often speak louder than words — and your host will help bridge any gaps.',
  },
];

export const cultureQuestions = [
  {
    question: 'Are there cultural protocols I should know about?',
    answer:
      'You can expect a warm welcome, cultural immersion, and traditional Fijian hospitality.',
  },
  {
    question: 'What should I bring?',
    answer: 'It is a traditional Fijian welcoming ceremony involving the presentation of kava.',
  },
  {
    question: 'How should I dress in the village?',
    answer: 'Some villages have limited internet and mobile service. Please check with your host.',
  },
  {
    question: 'Can I take photos and videos freely?',
    answer: "It's recommended to drink bottled or boiled water in most villages.",
  },
  {
    question: 'Are there cultural protocols I should know about?',
    answer:
      'You can expect a warm welcome, cultural immersion, and traditional Fijian hospitality.',
  },
  {
    question: 'What should I bring?',
    answer: 'It is a traditional Fijian welcoming ceremony involving the presentation of kava.',
  },
  {
    question: 'How should I dress in the village?',
    answer: 'Some villages have limited internet and mobile service. Please check with your host.',
  },
  {
    question: 'Can I take photos and videos freely?',
    answer: "It's recommended to drink bottled or boiled water in most villages.",
  },
];

export const HealthEmergenciesQuestions = [
  {
    question: 'Do I need travel insurance?',
    answer:
      'You can expect a warm welcome, cultural immersion, and traditional Fijian hospitality.',
  },
  {
    question: 'What if I get sick or injured?',
    answer: 'It is a traditional Fijian welcoming ceremony involving the presentation of kava.',
  },
  {
    question: 'What happens during a cyclone or emergency?',
    answer: 'Some villages have limited internet and mobile service. Please check with your host.',
  },
  {
    question: 'Who do I contact in an emergency?',
    answer: "It's recommended to drink bottled or boiled water in most villages.",
  },
  {
    question: 'Do I need travel insurance?',
    answer:
      'You can expect a warm welcome, cultural immersion, and traditional Fijian hospitality.',
  },
  {
    question: 'What if I get sick or injured?',
    answer: 'It is a traditional Fijian welcoming ceremony involving the presentation of kava.',
  },
  {
    question: 'What happens during a cyclone or emergency?',
    answer: 'Some villages have limited internet and mobile service. Please check with your host.',
  },
  {
    question: 'Who do I contact in an emergency?',
    answer: "It's recommended to drink bottled or boiled water in most villages.",
  },
];

export const HostQuestions = [
  {
    question: 'Who can become a host?',
    answer:
      'Anyone with a safe, clean home and a heart for hospitality can apply. You don’t need to be in the city or have fancy facilities — we welcome hosts from villages, farms, and remote communities across Fiji. If you’re open to sharing your space, your meals, and your way of life, you’re ready to host.',
  },
  {
    question: 'How do I register my home?',
    answer:
      'To apply, start by recording a short 2–3 minute video introducing yourself. Tell us your name, where you’re from, who you live with, and what makes your home or village special. Then, take a few clear photos of your space — including the bedroom, bathroom, kitchen, living area, and nearby surroundings. Once you’re ready, send your video and photos to us through Viber, WhatsApp, Messenger, or upload them directly through the Apply Now button at the bottom of the Become a Host page on www.fijianhost.com.',
  },
  {
    question: 'What is the verification process?',
    answer:
      'After we receive your video and photos, our team will review your submission to make sure your home is clean, safe, and ready to welcome guests. We may follow up with a quick call or visit if needed. If your application meets our standards, we’ll support you with any formal registration steps (if required), and once approved, your listing will go live on fijianhost.com.',
  },
  {
    question: 'Do I need internet or a smartphone to be a host?',
    answer:
      'No. If you don’t have access to the internet or a smartphone, we’ll work with someone you trust — a relative, village rep, or one of our team — to help manage your listing. You’ll still be the host, but we’ll handle the technical side so nothing holds you back.',
  },
  {
    question: 'Can someone help manage my listing for me?',
    answer:
      'Yes. If you’re not able to manage your listing yourself — whether due to lack of internet, devices, or confidence with technology — our fijianhost.com support team can manage your listing, bookings, and guest messages on your behalf. Guests will be informed that they’re corresponding with a fijianhost.com support staff member. Our team will relay guest messages, requests, and questions to you, and respond in consultation with you. We will call you to discuss any queries or special requests from the guest, so you remain informed and involved in every step. To support this, you’ll need to have at least a button phone so we can stay in touch with you by voice call.',
  },
  {
    question: 'What kinds of homes can be listed?',
    answer:
      'We accept all types — traditional bures, tin homes, wooden houses, or cement blocks. The home doesn’t need to be fancy. What matters is that it’s clean, safe, and ready to welcome a guest. A mosquito net, clean bed, and proper toilet are often enough to get started.',
  },
  {
    question: 'What support does fijianhost.com offer to new hosts?',
    answer:
      'We help with your registration, listing setup, and provide ongoing support. We’ll guide you through the process, answer your questions, and check in to see how things are going. If you need help preparing for your first guest, we’re just a phone call away.',
  },
  {
    question: 'How and when do I get paid?',
    answer:
      'All payments are made directly to your mobile wallet or bank account. In most cases, you will receive your payment either before the guest arrives or shortly after the guest checks out. The exact timing may vary slightly depending on the payment method in use, but we work to ensure that the process is smooth, timely, and secure for every host.',
  },
  {
    question: 'What if a guest damages something?',
    answer:
      'Let us know right away. We’ll speak to the guest and help sort things out. Guests are expected to treat your home with care, and if anything is damaged, we’ll work with both sides to find a fair solution.',
  },
  {
    question: 'Can I choose when my home is available?',
    answer:
      'Yes. You control your calendar and can block off any dates when your home is not available — whether for family obligations, farming, village events, or rest. We ask that you update your availability at least two months in advance wherever possible. This helps us manage bookings smoothly and avoid last-minute cancellations.',
  },
  {
    question: 'Do I need to be registered as a business or for tax?',
    answer:
      'Yes, we encourage all hosts to register — either as a sole trader or small business. This helps protect your income and ensures everything is legal. If you’re not sure how to register, we’ll guide you through the steps.',
  },
  {
    question: 'Can I host if I live in the same house as the guest?',
    answer:
      'Yes. Many hosts live on the same land or in the same house. Guests often enjoy this — they learn more and feel part of the home. Just make sure the guest has a clean, private place to sleep, and feels comfortable during their stay.',
  },
  {
    question: 'Can I decline a booking request?',
    answer:
      'Yes — hosting is always your choice. If something doesn’t feel right or you’re not available, you can decline a request. We understand that things like funerals or village work may come up — just let us know early so we can inform the guest.',
  },
  {
    question: 'What if I’ve already accepted a booking but something serious happens?',
    answer:
      'If there’s a death in the family, an emergency, or a big village event, and you can no longer host, contact us right away. We’ll help cancel the booking without penalty and talk to the guest. These things happen — and we’re here to support you.',
  },
  {
    question: 'How should I prepare my home for guests?',
    answer:
      'Keep the space tidy and welcoming. Provide clean bedding, safe drinking water, a place to wash, and a simple meal. Let your family know someone is coming — especially if they’re from overseas — and make the guest feel at ease.',
  },
  {
    question: 'Do I have to provide meals or activities?',
    answer:
      'We encourage you to offer meals, as they’re a big part of the experience. It doesn’t need to be special — just what your family normally eats. You can also offer optional low-risk activities such as fishing, hiking, farm visits, crabbing, or snorkeling — but only if you’re comfortable doing so and your guest is interested.',
  },
  {
    question: 'Is there someone I can contact if I need help?',
    answer:
      'Yes — our team is always available to help. Whether it’s a problem with a booking, a guest, or just a question, you can contact us anytime. We’ll walk with you every step of the way.',
  },
];

export const iconOfferingArr = [
  {
    key: 'private_room',
    icon: (
      <svg
        width="39"
        height="35"
        viewBox="0 0 39 35"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clip-path="url(#clip0_1268_6251)">
          <path
            d="M35.9326 19.8761H19.7191C18.6771 19.8761 17.7568 20.389 17.2025 21.1724H16.6517V20.7403C16.6517 19.1221 15.3629 17.8003 13.742 17.7195L13.5843 17.7156H9.20225L9.04449 17.7195C7.42358 17.8008 6.13483 19.1221 6.13483 20.7403V21.1724H4.82022V17.2835C4.82022 16.1277 3.89956 15.184 2.74183 15.1261L2.62921 15.123H2.19101L2.07839 15.1261C0.920663 15.184 0 16.1277 0 17.2835V32.407L0.00306742 32.518C0.0595955 33.6229 0.95791 34.5087 2.07839 34.5645L2.19101 34.5675H2.62921L2.74183 34.5645C3.86231 34.5087 4.76063 33.6229 4.81716 32.518L4.82022 32.407V31.1107H34.1798V33.7033L34.1815 33.7698C34.2157 34.4327 34.7547 34.9642 35.4269 34.9979L35.4944 34.9996H37.6854L37.7529 34.9979C38.4251 34.9642 38.9641 34.4327 38.9982 33.7698L39 33.7033V22.9008C39 21.2303 37.6267 19.8761 35.9326 19.8761ZM3.06742 32.407C3.06742 32.6455 2.8711 32.8391 2.62921 32.8391H2.19101C1.94912 32.8391 1.75281 32.6455 1.75281 32.407V17.2835C1.75281 17.045 1.94912 16.8514 2.19101 16.8514H2.62921C2.8711 16.8514 3.06742 17.045 3.06742 17.2835V32.407ZM7.88764 20.7403C7.88764 20.0243 8.47615 19.444 9.20225 19.444H13.5843C14.3104 19.444 14.8989 20.0243 14.8989 20.7403V21.1724H7.88764V20.7403ZM16.6517 29.3823H4.82022V22.9008H16.6517V29.3823ZM37.2472 33.2712H35.9326V31.1107H37.2472V33.2712ZM37.2472 29.3823H18.4045V22.9008C18.4045 22.1848 18.993 21.6045 19.7191 21.6045H35.9326C36.6587 21.6045 37.2472 22.1848 37.2472 22.9008V29.3823Z"
            fill="#FE7755"
          />
          <path
            d="M7.0116 11.6667H16.652V16.8519H18.4049V11.6667H28.0453V16.8519H29.7981V3.02469C29.7981 1.3542 28.4248 0 26.7307 0H8.3262C6.63211 0 5.25879 1.3542 5.25879 3.02469V16.8519H7.0116V11.6667ZM18.4049 1.72839H26.7307C27.4568 1.72839 28.0453 2.3087 28.0453 3.02469V9.93827H18.4049V1.72839ZM7.0116 3.02469C7.0116 2.3087 7.6001 1.72839 8.3262 1.72839H16.652V9.93827H7.0116V3.02469Z"
            fill="#FE7755"
          />
        </g>
        <defs>
          <clipPath id="clip0_1268_6251">
            <rect width="39" height="35" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
  },
  {
    key: 'shared_room',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M37.7539 0C38.2195 2.03514e-08 38.6026 0.354109 38.6484 0.807617L38.6523 0.899414V38.6523C38.6523 39.1488 38.2503 39.5508 37.7539 39.5508C37.2575 39.5508 36.8555 39.1488 36.8555 38.6523V33.2578H26.5176V37.7539C26.5176 38.2503 26.1156 38.6523 25.6191 38.6523C25.1227 38.6523 24.7207 38.2503 24.7207 37.7539V33.2578H1.79688V39.1016C1.79682 39.598 1.39484 40 0.898438 40C0.402081 39.9999 5.76443e-05 39.5979 0 39.1016V19.3262L0.00390625 19.2344C0.0498064 18.781 0.432977 18.4268 0.898438 18.4268C1.36395 18.4268 1.74707 18.781 1.79297 19.2344L1.79688 19.3262V21.5732H24.7207V18.877L24.7246 18.7852C24.7707 18.332 25.1538 17.9785 25.6191 17.9785C26.0845 17.9785 26.4676 18.332 26.5137 18.7852L26.5176 18.877V26.0684H36.8555V15.2803H13.9316V18.4277C13.9316 18.9242 13.5296 19.3262 13.0332 19.3262C12.5368 19.3262 12.1348 18.9242 12.1348 18.4277V0.899414L12.1387 0.807617C12.1846 0.354285 12.5677 0.00097695 13.0332 0.000976562C13.4987 0.000976583 13.8818 0.354285 13.9277 0.807617L13.9316 0.899414V2.69629H36.8555V0.899414L36.8594 0.807617C36.9052 0.354109 37.2883 8.36331e-07 37.7539 0ZM1.79688 31.4609H7.6416V27.8652H1.79688V31.4609ZM9.43848 31.4609H16.6299V27.8652H9.43848V31.4609ZM18.4268 31.4609H24.7207V27.8652H18.4268V31.4609ZM26.5176 31.4609H36.8555V27.8652H26.5176V31.4609ZM1.79688 26.0684H24.7207V23.3701H1.79688V26.0684ZM13.9316 13.4834H19.7764V8.53906H13.9316V13.4834ZM21.5732 13.4834H28.3154V8.53906H21.5732V13.4834ZM30.1123 13.4834H36.8555V8.53906H30.1123V13.4834ZM13.9316 6.74219H36.8555V4.49316H13.9316V6.74219Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },
  {
    key: 'floor_bed',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M25.8181 5C28.9344 5 31.7323 6.90622 32.8679 9.80267C32.8679 9.80267 38.8949 24.5902 39.088 25.6667C39.2812 26.7431 39.0881 34.1111 39.0881 34.1111C38.8983 34.4969 38.885 35 38.3929 35C37.9008 35 37.5023 34.6022 37.5023 34.1111V33.2222H1.43176V34.1111C1.43176 34.6022 1.0332 35 0.541127 35C0.0490537 35 0.0612063 34.6022 0.0612063 34.1111C0.0612063 34.1111 -0.0764217 26.9297 0.0610771 25.6667C0.198576 24.4037 5.8858 9.93644 5.8858 9.93644L5.99401 9.66133C7.16207 6.84889 9.91723 5 12.9868 5H25.8181ZM1.43176 31.4444H37.5023V27.4444H1.43176V31.4444ZM5.0869 17.2222L1.96035 25.6667H37.1754L33.8645 17.2222H5.0869ZM12.9868 6.77778C10.6391 6.77778 8.5323 8.19156 7.639 10.3427L7.55617 10.5529L5.74508 15.4449H33.1671L31.2091 10.4507C30.3407 8.236 28.201 6.77822 25.8176 6.77822L12.9868 6.77778Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },
  {
    key: 'proper_bed',
    icon: (
      <svg
        width="41"
        height="40"
        viewBox="0 0 41 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M40.0462 23.9911C40.0462 23.9827 40.0453 23.9748 40.0449 23.9664C40.044 23.9524 40.044 23.9388 40.0427 23.9247C40.0418 23.9177 40.04 23.9106 40.0392 23.9032C40.0383 23.8979 40.0378 23.8922 40.037 23.8869C40.0334 23.8636 40.0295 23.8408 40.0246 23.8184C40.0233 23.8135 40.022 23.8091 40.0207 23.8043C40.0194 23.799 40.018 23.7938 40.0167 23.7885C40.0119 23.77 40.0066 23.7516 40.0004 23.7336C39.9996 23.7305 39.9982 23.7279 39.9974 23.7248C39.9758 23.6624 39.9476 23.6035 39.9133 23.5486C39.9115 23.5459 39.9102 23.5438 39.9085 23.5411L37.0642 18.7342C37.0642 18.7342 37.0616 18.7306 37.0602 18.7284C37.0488 18.7096 37.0369 18.6911 37.0242 18.6731C37.0202 18.6674 37.0162 18.6612 37.0118 18.6555C37.0022 18.6423 36.9916 18.63 36.981 18.6173C36.9643 18.5975 36.9471 18.5786 36.9291 18.5606C36.9194 18.5509 36.9102 18.5408 36.9001 18.5316C36.8891 18.5215 36.8772 18.5123 36.8657 18.5026C36.8565 18.4947 36.8472 18.4868 36.8376 18.4793C36.827 18.4714 36.816 18.4639 36.805 18.4565C36.7927 18.4481 36.7808 18.4398 36.768 18.4319C36.7588 18.4261 36.7495 18.4213 36.7403 18.416C36.7249 18.4072 36.7091 18.3985 36.6932 18.3906C36.6849 18.3866 36.6765 18.3826 36.6681 18.3791C36.6514 18.3717 36.6347 18.3646 36.6175 18.358C36.6087 18.355 36.5999 18.3519 36.5911 18.3488C36.5731 18.3427 36.5546 18.3365 36.5361 18.3317C36.5277 18.3295 36.5189 18.3277 36.5101 18.3255C36.493 18.3216 36.4754 18.3172 36.4578 18.3145C36.4459 18.3123 36.434 18.311 36.4221 18.3093C36.4138 18.3079 36.4054 18.3066 36.397 18.3057L36.3786 18.3044C36.3632 18.3031 36.3473 18.3027 36.3315 18.3022L36.3068 18.3009H30.8058C30.8806 18.1717 30.8058 18.0219 30.8058 17.8615V13.0282C30.8058 12.5427 30.4115 12.1494 29.9256 12.1494C29.8882 12.1494 29.8517 12.1525 29.8156 12.1573C29.7795 12.1529 29.743 12.1494 29.7056 12.1494H10.3419C10.3045 12.1494 10.268 12.1525 10.2319 12.1573C10.1958 12.1529 10.1593 12.1494 10.1219 12.1494C9.63561 12.1494 9.24174 12.5427 9.24174 13.0282V17.8615C9.24174 18.0219 9.17755 18.1609 9.25236 18.2901L3.74026 18.3009C3.7319 18.3009 3.72354 18.3018 3.71518 18.3022C3.69934 18.3027 3.68393 18.3031 3.66853 18.3044C3.65489 18.3057 3.64168 18.3071 3.62804 18.3088C3.61308 18.3106 3.59856 18.3132 3.58359 18.3159C3.56775 18.3185 3.55235 18.322 3.53694 18.326C3.52682 18.3282 3.5167 18.3308 3.50702 18.3334C3.48854 18.3387 3.47049 18.344 3.45245 18.3501C3.44365 18.3532 3.43485 18.3563 3.42604 18.3594C3.40844 18.3659 3.39128 18.373 3.37455 18.3809C3.36531 18.3853 3.35607 18.3897 3.34727 18.3941C3.33187 18.4015 3.31646 18.4094 3.3015 18.4182C3.29006 18.4248 3.27906 18.4319 3.26805 18.4389C3.25661 18.4464 3.24473 18.4538 3.23373 18.4617C3.22097 18.4705 3.20908 18.4797 3.19676 18.489C3.18664 18.4969 3.17652 18.5052 3.16684 18.514C3.15451 18.5246 3.14263 18.5356 3.13119 18.5465C3.12327 18.554 3.11535 18.5615 3.10786 18.5694C3.09554 18.5821 3.08366 18.5953 3.07222 18.6089C3.06386 18.6186 3.05593 18.6283 3.04801 18.6379C3.03921 18.6494 3.03041 18.6603 3.02205 18.6722C3.01149 18.6871 3.0018 18.703 2.99212 18.7183C2.98904 18.7232 2.98552 18.728 2.98288 18.7328L0.150508 23.5231C0.147868 23.5266 0.146107 23.5306 0.143467 23.5341C0.135545 23.5464 0.128064 23.5587 0.120583 23.5714C0.113981 23.5829 0.10694 23.5943 0.100779 23.6057C0.0941777 23.618 0.0884566 23.6308 0.0827355 23.6435C0.0770145 23.6562 0.0708533 23.6685 0.0655723 23.6817C0.0607314 23.6931 0.0563306 23.705 0.0523698 23.7169C0.0470889 23.7309 0.0422479 23.745 0.0378471 23.7595C0.0215641 23.8127 0.010562 23.868 0.00484091 23.9251L0 24.0148V34.1208C0 34.6063 0.393874 34.9996 0.880165 34.9996H39.1674L39.2571 34.9948C39.2862 34.9917 39.3152 34.9873 39.3434 34.9816C39.3491 34.9803 39.3544 34.9785 39.3601 34.9772C39.3812 34.9724 39.4019 34.9675 39.4226 34.9614C39.4371 34.957 39.4512 34.9517 39.4653 34.9469C39.4772 34.9425 39.4891 34.9385 39.5005 34.9337C39.5137 34.9284 39.526 34.9223 39.5388 34.9166C39.5516 34.9108 39.5643 34.9051 39.5766 34.8985C39.5885 34.8924 39.5995 34.8854 39.611 34.8788C39.6237 34.8713 39.636 34.8638 39.6484 34.8559C39.6589 34.8489 39.6686 34.8419 39.6787 34.8344C39.6911 34.8256 39.7029 34.8173 39.7148 34.808C39.7241 34.8006 39.7329 34.7931 39.7421 34.7852C39.7544 34.7746 39.7668 34.7636 39.7786 34.7522C39.7866 34.7448 39.794 34.7373 39.8015 34.7294C39.813 34.7175 39.8235 34.7056 39.8341 34.6933C39.8416 34.6846 39.849 34.6762 39.8561 34.667C39.8662 34.6542 39.8755 34.6415 39.8847 34.6283C39.8917 34.6182 39.8988 34.6081 39.9054 34.598C39.9129 34.5861 39.9203 34.5743 39.9274 34.5624C39.934 34.551 39.9406 34.54 39.9468 34.5286C39.9547 34.5136 39.9617 34.4983 39.9688 34.4829C39.9732 34.4736 39.9776 34.4644 39.9815 34.4548C39.9881 34.4389 39.9934 34.4222 39.9991 34.406C40.0026 34.3959 40.0062 34.3862 40.0092 34.3761C40.0145 34.3581 40.0194 34.3401 40.0233 34.3221C40.0255 34.3124 40.0277 34.3032 40.0299 34.2935C40.0356 34.2645 40.0409 34.2355 40.0436 34.2056L40.0475 34.1217V24.0157L40.0462 23.9919V23.9911ZM11.0021 17.8633V13.9087H29.0455V17.8633C29.0455 18.0237 28.9706 18.1735 29.0455 18.3027H11.0021C11.0769 18.1735 11.0021 18.0237 11.0021 17.8633ZM4.24372 20.0602H35.8042L37.6244 23.136H2.42398L4.24372 20.0602ZM38.2872 33.242H1.76033V31.9239H38.2872V33.242ZM38.2872 30.1663H1.76033V24.8936H38.2872V30.1663Z"
          fill="#FE7755"
        />
        <path
          d="M34.4161 6.00483C34.4606 6.00923 34.5037 6.01758 34.546 6.02856C34.6164 6.01055 34.6903 6.00044 34.7664 6.00044C35.2224 6.00044 35.5969 6.34624 35.6418 6.78959L35.6466 6.87923V16.1065C35.6466 16.592 35.2523 16.9853 34.7664 16.9853C34.2806 16.9853 33.8863 16.592 33.8863 16.1065V7.75757H6.60115V16.106C6.60115 16.5916 6.20684 16.9848 5.72099 16.9848C5.23513 16.9848 4.84082 16.5916 4.84082 16.106V6.87879L4.84566 6.78915C4.85138 6.73203 4.86238 6.67667 4.87867 6.6235C4.88307 6.609 4.88835 6.59494 4.89319 6.58088C4.89759 6.56901 4.90155 6.55715 4.90639 6.54573C4.91167 6.53298 4.91783 6.52024 4.92356 6.5075C4.92972 6.49476 4.935 6.48201 4.9416 6.46971C4.94776 6.45785 4.9548 6.44686 4.9614 6.43544C4.96888 6.4227 4.97637 6.41039 4.98429 6.39809C4.99133 6.38754 4.99837 6.37788 5.00585 6.36777C5.01465 6.35547 5.02301 6.34361 5.0327 6.33174C5.04018 6.32251 5.04766 6.31373 5.05558 6.3045C5.06614 6.2922 5.07714 6.27989 5.08859 6.26803C5.09607 6.26012 5.10355 6.25265 5.11147 6.24518C5.12335 6.23376 5.13524 6.22321 5.14756 6.21267C5.15636 6.2052 5.16472 6.19773 5.17396 6.1907C5.18673 6.18059 5.19949 6.17136 5.21269 6.16214C5.22281 6.15511 5.23293 6.14808 5.24306 6.14148C5.2545 6.13401 5.26682 6.12655 5.2787 6.11951C5.29014 6.11292 5.30115 6.10633 5.31259 6.10018C5.32755 6.09227 5.34295 6.08524 5.35836 6.07821C5.3676 6.07382 5.37728 6.06942 5.38652 6.06547C5.40237 6.05888 5.41909 6.05361 5.43537 6.04789C5.44549 6.04438 5.45518 6.04086 5.4653 6.03779C5.4829 6.03252 5.50138 6.02768 5.51943 6.02373C5.52867 6.02153 5.53835 6.01933 5.54759 6.01714C5.60348 6.00615 5.66113 6 5.72055 6H34.3259L34.4161 6.00483Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },

  {
    key: 'mosquito_net',
    icon: (
      <svg
        width="40"
        height="37"
        viewBox="0 0 40 37"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clip-path="url(#clip0_1268_6255)">
          <path
            d="M39.9427 34.2209C38.9283 28.42 38.0487 22.596 37.1897 16.7689C36.5063 12.1316 35.859 7.48836 35.1947 2.84797C35.1379 2.45112 34.9107 1.96663 35.0546 1.68069C35.2466 1.29884 35.7816 1.57448 36.1646 1.5222C36.567 1.46738 36.8916 1.3136 36.9411 0.860651C36.9911 0.405032 36.819 0.0744475 36.3244 0.0263672C36.131 0.128633 35.9231 0.0809346 35.7223 0.0810618C25.256 0.0836057 14.7896 0.0836057 4.3233 0.0810618C4.12241 0.0810618 3.91466 0.128125 3.72101 0.0271304C3.26908 0.081825 3.07714 0.382772 3.11124 0.813461C3.14643 1.25712 3.44433 1.47476 3.8553 1.51279C4.07826 1.53339 4.30638 1.53759 4.5286 1.51317C4.94755 1.467 5.09165 1.56825 5.01009 2.06266C4.71808 3.8326 4.4809 5.61221 4.2193 7.3875C3.57911 11.7325 2.94518 16.0786 2.29334 20.4217C1.57258 25.2233 0.85868 30.0265 0.0190766 34.8079C-0.0335366 35.1077 0.0242275 35.3336 0.173237 35.577C1.07122 37.0429 3.34793 37.4723 4.67786 36.4125C5.04284 36.1217 5.28714 35.7708 5.32908 35.2515C5.45847 33.6503 5.64501 32.0541 5.78151 30.4535C6.13177 26.3458 6.47394 22.2373 6.79857 18.1275C6.83524 17.6628 7.00829 17.4451 7.41853 17.2591C10.7544 15.747 13.7945 13.7529 16.4532 11.1595C17.806 9.83985 19.0031 8.38052 20.0229 6.69249C20.2229 7.00896 20.3826 7.27213 20.5528 7.52767C20.7267 7.78868 20.9045 8.04752 21.0918 8.29835C23.9828 12.1723 27.7284 14.9041 31.9709 16.9346C32.8477 17.3544 33.2474 17.764 33.3227 18.8759C33.6772 24.1062 34.1864 29.3252 34.6426 34.5481C34.7598 35.8897 35.274 36.6066 36.3327 36.866C37.1738 37.072 38.0003 36.9766 38.7835 36.6003C39.8186 36.1027 40.1459 35.3837 39.9425 34.221L39.9427 34.2209ZM5.32418 18.8617C5.01524 22.3345 4.70288 25.8272 4.39443 29.3203C4.24199 31.0461 4.09421 32.7723 3.94949 34.4988C3.88805 35.2323 3.72493 35.4086 3.02109 35.473C2.68641 35.5037 2.37465 35.4035 2.06412 35.3002C1.60299 35.1468 1.45987 34.8736 1.5646 34.3391C2.3836 30.1579 2.95462 25.9325 3.60634 21.7216C3.76442 20.6999 3.9127 19.6767 4.07287 18.6555C4.11518 18.3857 5.15113 17.9203 5.31106 18.1081C5.49637 18.326 5.27463 18.5938 5.3243 18.8617H5.32418ZM21.565 6.30518C21.2062 5.76243 20.9272 5.17631 20.7159 4.55394C20.6052 4.22806 20.3785 4.02136 20.0302 4.01602C19.7193 4.01119 19.468 4.13711 19.3541 4.45777C18.4634 6.9633 16.773 8.85382 14.8933 10.5705C11.9043 13.3001 8.5261 15.3523 4.72017 16.6165C4.62745 16.6473 4.52725 16.6542 4.36929 16.6835C5.04725 12.0432 5.71356 7.49129 6.37558 2.93866C6.44181 2.48304 6.33278 1.87822 6.63068 1.61442C6.95641 1.32606 7.53099 1.5208 7.99592 1.52055C16.2882 1.51559 24.5805 1.52322 32.8726 1.50338C33.3741 1.50223 33.5267 1.66784 33.594 2.14483C34.2575 6.84856 34.9336 11.5503 35.622 16.2502C35.6825 16.6633 35.6225 16.7321 35.24 16.5932C29.6908 14.5779 24.945 11.4182 21.5651 6.30518H21.565ZM38.132 35.2156C37.8081 35.3565 37.4729 35.4308 37.1705 35.4561C36.3628 35.4486 36.1663 35.2202 36.0947 34.3964C35.8203 31.2403 35.523 28.0863 35.2481 24.9302C35.0647 22.8257 34.9104 20.7186 34.7294 18.614C34.6929 18.1887 34.7231 18.0363 35.231 18.1261C35.8012 18.2269 35.9787 18.4857 36.0601 19.0422C36.812 24.1761 37.5114 29.3194 38.515 34.4099C38.594 34.8107 38.4923 35.0589 38.132 35.2156Z"
            fill="#FE7755"
          />
          <path
            d="M12.4178 16.6085C12.2569 16.6085 12.0911 16.6402 11.9372 16.559C10.8083 16.7085 9.97287 17.5447 9.83698 18.6794C9.74672 19.4334 9.64431 20.1881 9.60261 20.9458C9.53136 22.241 9.23542 23.4181 8.36308 24.4191C7.93126 24.9148 7.63164 25.5357 7.28138 26.106C7.10355 26.3958 7.02126 26.7119 7.02224 27.0661C7.02984 29.6474 7.028 32.2288 7.02052 34.81C7.01905 35.3171 7.15702 35.6963 7.70633 35.739C8.1796 35.7758 8.4634 35.4295 8.47664 34.8171C8.49173 34.1194 8.49541 33.4216 8.50411 32.7238C8.64895 32.5403 8.84788 32.6294 9.01921 32.6293C16.3521 32.6242 23.6848 32.6243 31.0177 32.6283C31.1932 32.6283 31.3875 32.5568 31.5433 32.7121C31.651 33.444 31.5596 34.1805 31.5916 34.914C31.6087 35.3053 31.7553 35.6318 32.1436 35.7222C32.5427 35.8151 32.8606 35.6655 32.9928 35.2231C32.9061 32.9376 32.9729 30.6513 32.9591 28.3654C32.9558 27.8178 32.8993 27.2667 33.0026 26.7214C32.9775 26.3446 32.7643 26.0507 32.5752 25.7574C32.111 25.0374 31.649 24.3128 31.1339 23.6327C30.7877 23.1755 30.6395 22.6797 30.5905 22.1235C30.4999 21.0936 30.4008 20.0644 30.3054 19.0349C30.1772 17.6527 29.3545 16.7248 28.1091 16.5576C27.9557 16.6407 27.7896 16.6082 27.6288 16.6082M10.9764 21.3255C11.0804 20.6369 11.1452 19.9408 11.1994 19.2457C11.2675 18.3739 11.5803 18.0482 12.4429 18.0472C16.1802 18.0428 19.9177 18.0445 23.6551 18.0443C24.9622 18.0443 26.2693 18.0417 27.5763 18.0456C28.5082 18.0484 28.7797 18.3071 28.8538 19.2557C28.9277 20.2031 29.1159 21.1401 29.0883 22.0964C29.0298 22.2093 28.961 22.2203 28.8811 22.1187C28.6244 22.0128 28.356 22.0627 28.0933 22.0626C22.5994 22.0596 17.1056 22.0495 11.6118 22.0763C11.007 22.0792 10.8929 21.879 10.9766 21.3253L10.9764 21.3255ZM10.5888 23.6006C10.6841 23.3822 10.8742 23.4797 11.0186 23.4795C17.0365 23.4741 23.0543 23.4742 29.0722 23.4779C29.2032 23.4779 29.3643 23.4084 29.4556 23.5835C29.8817 24.4769 30.5358 25.2177 31.0617 26.1266H8.98855C9.53283 25.2673 10.0608 24.4339 10.5886 23.6005L10.5888 23.6006ZM31.593 30.9216C31.6014 31.2086 31.4617 31.2914 31.2152 31.2716C31.1138 31.2635 31.0112 31.2702 30.9091 31.2702C23.6409 31.2705 16.3725 31.2649 9.10432 31.2836C8.58935 31.2848 8.44316 31.1411 8.46757 30.6167C8.51368 29.6239 8.49504 28.6279 8.50325 27.6333C8.6568 27.4724 8.85192 27.5465 9.02669 27.5465C16.3557 27.5425 23.6847 27.5425 31.0137 27.546C31.1908 27.546 31.3832 27.4822 31.5435 27.6239C31.6497 28.7218 31.5607 29.8228 31.593 30.9218V30.9216Z"
            fill="#FE7755"
          />
        </g>
        <defs>
          <clipPath id="clip0_1268_6255">
            <rect width="40" height="37" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
  },

  {
    key: 'shared_bathroom',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11.3203 13.6953C12.382 13.803 13.2109 14.6999 13.2109 15.79V23.79L13.1992 24.0049C13.1124 24.8606 12.5129 25.5628 11.7129 25.8037L11.5635 33.9336L11.5488 34.1465C11.4325 35.123 10.6495 35.8912 9.6709 35.9893L9.45898 36H4.75L4.53711 35.9893C3.55874 35.891 2.77551 35.1229 2.65918 34.1465L2.64453 33.9336L2.49414 25.8027C1.69578 25.5608 1.09743 24.8594 1.01074 24.0049L1 23.79V15.79C1 14.7001 1.82822 13.8033 2.88965 13.6953L3.10547 13.6846H11.1055L11.3203 13.6953ZM19.1074 4.84277C19.5725 4.84277 19.9492 5.21949 19.9492 5.68457V35.1582L19.9453 35.2441C19.9023 35.6689 19.5435 36 19.1074 36C18.6713 36 18.3126 35.6689 18.2695 35.2441L18.2656 35.1582V5.68457C18.2656 5.21949 18.6423 4.84277 19.1074 4.84277ZM36.7129 13.6943C37.7272 13.7928 38.5391 14.6128 38.6084 15.6494L39.1699 24.0713C39.2456 25.2103 38.4029 26.1871 37.2939 26.3047L37.0684 26.3164H36.9707L36.8301 33.9336L36.8154 34.1465C36.6991 35.123 35.9161 35.8912 34.9375 35.9893L34.7256 36H30.0166L29.8037 35.9893C28.8253 35.891 28.0421 35.1229 27.9258 34.1465L27.9111 33.9336L27.7705 26.3164H27.502L27.2744 26.3037C26.2281 26.1916 25.4222 25.3157 25.3975 24.2637L25.4043 24.0361L26.1055 15.6152C26.1907 14.5922 26.9981 13.7904 28.001 13.6943L28.2041 13.6846H36.5078L36.7129 13.6943ZM4.3291 33.9023L4.33887 33.9863C4.38066 34.1753 4.54916 34.3164 4.75 34.3164H9.45898C9.68833 34.3162 9.87564 34.1317 9.87988 33.9023L10.0205 26.3164H4.18848L4.3291 33.9023ZM29.5957 33.9023L29.6055 33.9863C29.6473 34.1753 29.8158 34.3164 30.0166 34.3164H34.7256C34.9549 34.3162 35.1422 34.1317 35.1465 33.9023L35.2871 26.3164H29.4551L29.5957 33.9023ZM28.2041 15.3691C28.0124 15.3691 27.8479 15.4973 27.7979 15.6758L27.7842 15.7549L27.082 24.1758C27.0616 24.4213 27.2556 24.6318 27.502 24.6318H37.0684C37.3119 24.6318 37.5055 24.4256 37.4893 24.1826L36.9277 15.7617C36.913 15.5407 36.7293 15.3693 36.5078 15.3691H28.2041ZM3.10547 15.3691C2.87292 15.3691 2.68457 15.5575 2.68457 15.79V23.79C2.68468 24.0225 2.873 24.2109 3.10547 24.2109H11.1055C11.3379 24.2108 11.5263 24.0224 11.5264 23.79V15.79C11.5264 15.5576 11.3379 15.3693 11.1055 15.3691H3.10547ZM7.20312 4.00586C9.42804 4.11842 11.1973 5.95805 11.1973 8.21094L11.1924 8.42676C11.0799 10.6516 9.24001 12.4207 6.9873 12.4209L6.77051 12.416C4.61727 12.307 2.89116 10.58 2.78223 8.42676L2.77637 8.21094C2.77637 5.88553 4.6619 4 6.9873 4L7.20312 4.00586ZM32.4658 4.00586C34.6907 4.11842 36.46 5.95805 36.46 8.21094L36.4551 8.42676C36.3425 10.6516 34.5027 12.4207 32.25 12.4209L32.2041 12.4189C32.1895 12.4191 32.1748 12.4209 32.1602 12.4209L31.9434 12.416C29.7901 12.307 28.064 10.58 27.9551 8.42676L27.9492 8.21094C27.9492 5.88553 29.8347 4 32.1602 4L32.2041 4.00098C32.2194 4.00081 32.2347 4 32.25 4L32.4658 4.00586ZM6.9873 5.68457C5.59206 5.68457 4.46094 6.81569 4.46094 8.21094C4.46116 9.60599 5.5922 10.7373 6.9873 10.7373C8.38222 10.7371 9.51345 9.60586 9.51367 8.21094C9.51367 6.81583 8.38236 5.68479 6.9873 5.68457ZM32.2041 5.68652C30.83 5.71102 29.7236 6.83103 29.7236 8.21094C29.7239 9.59098 30.8307 10.7104 32.2051 10.7344C33.5793 10.7102 34.6863 9.59084 34.6865 8.21094C34.6865 6.83052 33.5789 5.71021 32.2041 5.68652Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },
  {
    key: 'private_bathroom',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M30.0088 0.00878906C33.5313 0.187371 36.332 3.10012 36.332 6.66699V40H34.4277V6.66699C34.4277 4.11935 32.427 2.03888 29.9111 1.91113L29.666 1.9043H18.1279C16.467 2.3273 15.3549 3.338 14.624 4.47363C14.0289 5.39831 13.7025 6.38584 13.5566 7.14258H14.7637L15.1318 7.15234C18.9056 7.34388 21.907 10.4638 21.9072 14.2852V15.2373H22.5244L22.6211 15.2432C23.1015 15.2918 23.4766 15.6972 23.4766 16.1904C23.4764 16.6836 23.1015 17.0891 22.6211 17.1377L22.5244 17.1426H3.95215C3.42635 17.1424 3.00013 16.7162 3 16.1904C3 15.6645 3.42627 15.2374 3.95215 15.2373H4.76367V14.2852C4.76388 10.4637 7.76507 7.34366 11.5391 7.15234L11.6221 7.14941C11.7714 6.12035 12.1902 4.73544 13.0225 3.44238C13.9733 1.96523 15.4861 0.577996 17.7607 0.0302734V0H29.666L30.0088 0.00878906ZM7.7627 33.8096C8.25574 33.8097 8.66124 34.1848 8.70996 34.665L8.71484 34.7627V37.1436C8.71469 37.6693 8.28848 38.0956 7.7627 38.0957C7.23687 38.0956 6.80972 37.6694 6.80957 37.1436V34.7627L6.81543 34.665C6.86417 34.1848 7.26964 33.8097 7.7627 33.8096ZM13.4756 33.8096C13.9686 33.8097 14.3741 34.1848 14.4229 34.665L14.4277 34.7627V37.1436C14.4276 37.6694 14.0014 38.0956 13.4756 38.0957C12.9497 38.0957 12.5226 37.6694 12.5225 37.1436V34.7627L12.5283 34.665C12.5771 34.1848 12.9824 33.8096 13.4756 33.8096ZM19.667 33.8096C20.16 33.8097 20.5655 34.1848 20.6143 34.665L20.6191 34.7627V37.1436C20.619 37.6693 20.1928 38.0955 19.667 38.0957C19.1411 38.0957 18.714 37.6694 18.7139 37.1436V34.7627L18.7197 34.665C18.7685 34.1848 19.1739 33.8096 19.667 33.8096ZM7.7627 26.667C8.25574 26.6671 8.66124 27.0422 8.70996 27.5225L8.71484 27.6201V30.001C8.7146 30.5267 8.28843 30.953 7.7627 30.9531C7.23693 30.953 6.80981 30.5267 6.80957 30.001V27.6201L6.81543 27.5225C6.86417 27.0422 7.26964 26.6671 7.7627 26.667ZM13.4756 26.667C13.9686 26.6671 14.3741 27.0422 14.4229 27.5225L14.4277 27.6201V30.001C14.4276 30.5268 14.0014 30.953 13.4756 30.9531C12.9497 30.9531 12.5226 30.5269 12.5225 30.001V27.6201L12.5283 27.5225C12.5771 27.0422 12.9824 26.667 13.4756 26.667ZM19.667 26.667C20.16 26.6671 20.5655 27.0423 20.6143 27.5225L20.6191 27.6201V30.001C20.6189 30.5267 20.1927 30.953 19.667 30.9531C19.1411 30.9531 18.7141 30.5268 18.7139 30.001V27.6201L18.7197 27.5225C18.7685 27.0422 19.1739 26.667 19.667 26.667ZM7.7627 20C8.25574 20.0001 8.66124 20.3752 8.70996 20.8555L8.71484 20.9531V23.334C8.71464 23.8597 8.28845 24.286 7.7627 24.2861C7.2369 24.286 6.80977 23.8597 6.80957 23.334V20.9531L6.81543 20.8555C6.86417 20.3752 7.26964 20.0001 7.7627 20ZM13.4756 20C13.9686 20.0001 14.3741 20.3752 14.4229 20.8555L14.4277 20.9531V23.334C14.4276 23.8598 14.0014 24.286 13.4756 24.2861C12.9497 24.2861 12.5226 23.8599 12.5225 23.334V20.9531L12.5283 20.8555C12.5771 20.3752 12.9824 20 13.4756 20ZM19.667 20C20.16 20.0002 20.5655 20.3753 20.6143 20.8555L20.6191 20.9531V23.334C20.6189 23.8597 20.1927 24.286 19.667 24.2861C19.1411 24.2861 18.7141 23.8598 18.7139 23.334V20.9531L18.7197 20.8555C18.7685 20.3752 19.1739 20 19.667 20ZM11.9062 9.04785C9.01359 9.04806 6.66916 11.3925 6.66895 14.2852V15.2373H20.002V14.2852C20.0017 11.3924 17.6565 9.04785 14.7637 9.04785H11.9062Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },

  {
    key: 'indoor_bathroom',
    icon: (
      <svg
        width="28"
        height="40"
        viewBox="0 0 28 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clip-path="url(#clip0_1268_6340)">
          <path
            d="M11.2143 0.738656C12.5429 0.828089 13.5999 2.28279 13.5999 4.06431V15.4812H25.0966C26.0885 15.4812 26.893 16.5463 26.893 17.8598V20.2868C27.3374 20.8876 27.6115 20.987 27.6115 22.6168C27.6115 24.2466 26.9918 24.9953 26.9918 24.9953C26.8947 25.257 26.7848 25.5119 26.6634 25.7584C26.122 26.8582 25.3445 27.8467 24.5002 28.7034C23.0107 30.2148 21.219 31.4083 19.8552 32.1404L20.3294 38.2166L20.4117 39.266H5.04665L5.30532 38.0558C6.31344 33.3354 5.67214 29.7115 4.56989 27.1788C3.44645 24.5967 1.4417 23.2457 1.04004 22.6163C0.411523 21.0609 1.58901 20.1893 2.40994 19.6413C2.02373 19.0871 1.77762 18.3583 1.74673 17.5548L1.74349 17.3836V4.06431C1.74385 2.28279 2.80047 0.827613 4.12906 0.738656L4.2584 0.734375H11.0846L11.2143 0.738656ZM3.54022 21.1897C3.17987 21.1897 2.10923 21.046 2.66539 21.7843C3.22154 22.5226 4.91946 24.1719 5.81513 26.2298C7.0072 28.9698 7.68874 32.7103 6.91666 37.3637H18.8183L18.3696 31.6152L18.3124 30.8812L18.8348 30.6248C20.1451 29.9831 22.0834 28.7582 23.6257 27.1931C24.3967 26.411 25.0387 25.5743 25.4598 24.7194C26.3792 23.3237 26.1345 22.2367 26.1345 22.2367C26.0088 21.633 25.5924 21.1897 25.0966 21.1897H3.54022ZM13.5966 17.5553C13.5722 18.197 13.4102 18.7912 13.149 19.2869H25.0966C25.2187 19.2869 25.3384 19.2992 25.4559 19.3211V17.8598C25.4559 17.5972 25.2949 17.3841 25.0966 17.3841H13.5999L13.5966 17.5553ZM4.25876 2.6372C3.66345 2.6372 3.18094 3.27607 3.18094 4.06431V17.3841C3.18094 18.1723 3.66345 18.8112 4.25876 18.8112H11.0849C11.6803 18.8112 12.1628 18.1723 12.1628 17.3841V4.06431C12.1628 3.27607 11.6803 2.6372 11.0849 2.6372H4.25876Z"
            fill="#FE7755"
          />
        </g>
        <defs>
          <clipPath id="clip0_1268_6340">
            <rect
              width="26.9444"
              height="38.5321"
              fill="white"
              transform="translate(0.666992 0.734375)"
            />
          </clipPath>
        </defs>
      </svg>
    ),
  },

  {
    key: 'outdoor_bathroom',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M37.8887 15.1104L37.9561 15.1719C38.2716 15.4931 38.2985 16.0082 38.0049 16.3623C37.7112 16.7163 37.2001 16.7849 36.8262 16.5342L36.7539 16.4785L34.7764 14.8379V38.2217H37.4443L37.5352 38.2256C37.9832 38.2712 38.333 38.6502 38.333 39.1104C38.333 39.5705 37.9832 39.9495 37.5352 39.9951L37.4443 39.999H1.88867C1.39776 39.999 1 39.6013 1 39.1104C1 38.6194 1.39776 38.2217 1.88867 38.2217H4.55469V14.8398L2.57812 16.4795L1.44336 15.1113L19.666 0L37.8887 15.1104ZM6.33203 13.3662V38.2217H16.0264L14.8594 32H14.2295L14.1035 31.9941C13.5245 31.9352 13.0648 31.4745 13.0059 30.8955L12.999 30.7695C12.999 29.1818 13.9013 27.8052 15.2207 27.123V22.666H12.999V19.1113C12.9991 17.9609 13.8737 17.0142 14.9941 16.9004L15.2217 16.8887H24.5547L24.7812 16.9004C25.9019 17.014 26.7762 17.9607 26.7764 19.1113V22.666H16.998V26.6689L17.1016 26.667H22.6738L22.8857 26.6719C25.0532 26.782 26.7764 28.5747 26.7764 30.7695L26.7705 30.8955C26.7116 31.4747 26.251 31.9353 25.6719 31.9941L25.5459 32H25.3604L24.1934 38.2217H32.999V13.3643L31.3428 11.9912C31.3324 11.9926 31.322 11.995 31.3115 11.9961L31.2207 12H8.10938C8.06889 12 8.02928 11.9955 7.99023 11.9902L6.33203 13.3662ZM16.668 32L17.835 38.2217H22.3848L23.5518 32H16.668ZM17.1016 28.4443C16.0062 28.4443 15.0899 29.2029 14.8438 30.2227H24.9316C24.6855 29.2029 23.7692 28.4443 22.6738 28.4443H17.1016ZM15.2217 18.666C14.9764 18.6661 14.7765 18.8661 14.7764 19.1113V20.8887H24.999V19.1113C24.9989 18.866 24.8 18.6661 24.5547 18.666H15.2217ZM10.123 10.2227H29.21L19.666 2.30859L10.123 10.2227Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },

  {
    key: 'towels_provided',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M33.5186 5.00684C36.1602 5.14095 38.2607 7.32501 38.2607 10L38.2539 10.2568C38.1846 11.6274 37.5626 12.8513 36.6074 13.7129C37.8489 14.3689 38.6953 15.673 38.6953 17.1748C38.6949 18.8092 37.692 20.2079 36.2686 20.793C38.2631 22.2111 39.5653 24.5401 39.5654 27.1738C39.5652 31.2734 36.4124 34.6333 32.3994 34.9688C32.3551 34.9806 32.3095 34.9904 32.2627 34.9951L32.1738 35H5.2168C2.33559 34.9997 0 32.6635 0 29.7822C0.000144233 27.766 1.14443 26.0178 2.81836 25.1494C1.11651 23.9727 0.000198394 22.0089 0 19.7832C0 17.5875 1.08593 15.6457 2.74902 14.4639C1.11864 13.6402 0 11.9514 0 10C0 7.32472 2.10111 5.14053 4.74316 5.00684L5 5H33.2607L33.5186 5.00684ZM6.52148 15C3.88032 15.0002 1.73828 17.142 1.73828 19.7832C1.7386 22.4242 3.88051 24.5652 6.52148 24.5654H32.1738L32.2627 24.5703C32.701 24.615 33.043 24.9854 33.043 25.4355C33.0426 25.8853 32.7008 26.2551 32.2627 26.2998L32.1738 26.3047H6.52148C6.50911 26.3047 6.49674 26.3038 6.48438 26.3037H5.2168C3.29621 26.304 1.73849 27.8616 1.73828 29.7822C1.73828 31.703 3.29608 33.2604 5.2168 33.2607H31.7744C35.1198 33.2417 37.826 30.5236 37.8262 27.1738C37.8259 23.8124 35.1008 21.087 31.7393 21.0869H7.3916C6.91148 21.0869 6.52169 20.6978 6.52148 20.2178C6.52148 19.7375 6.91135 19.3486 7.3916 19.3486H34.7822C35.9825 19.3486 36.9566 18.375 36.957 17.1748C36.957 15.9742 35.9828 15 34.7822 15H6.52148ZM5 6.73926C3.19907 6.73926 1.73926 8.19907 1.73926 10C1.73926 11.8009 3.19907 13.2607 5 13.2607H33.2607C35.0617 13.2607 36.5215 11.8009 36.5215 10C36.5215 8.19907 35.0617 6.73926 33.2607 6.73926H5Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },

  {
    key: 'breakfast_only',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M20 0C31.0457 0 40 8.9543 40 20C40 31.0457 31.0457 40 20 40C8.9543 40 0 31.0457 0 20C0 8.9543 8.9543 0 20 0ZM20 2.05176C10.0872 2.05176 2.05176 10.0872 2.05176 20C2.05176 29.9128 10.0872 37.9482 20 37.9482C29.9128 37.9482 37.9482 29.9128 37.9482 20C37.9482 10.0872 29.9128 2.05176 20 2.05176ZM31.4111 7.10059C31.9774 7.10079 32.4365 7.55967 32.4365 8.12598V15.0488C32.4365 16.4935 31.7328 17.7732 30.6504 18.5664L31.3301 32.1523C31.3709 32.9759 30.7577 33.6747 29.9561 33.7588L29.793 33.7676H26.874L26.7109 33.7588C25.9096 33.6744 25.297 32.9757 25.3379 32.1523L26.001 18.8818C24.6418 18.1437 23.7188 16.7043 23.7188 15.0488V8.12598C23.7188 7.55954 24.1777 7.10059 24.7441 7.10059C25.3106 7.10061 25.7695 7.55956 25.7695 8.12598V15.0488C25.7695 16.3232 26.8028 17.3563 28.0771 17.3564C29.3516 17.3564 30.3848 16.3233 30.3848 15.0488V8.12598C30.3848 7.55954 30.8447 7.10059 31.4111 7.10059ZM10.8613 8.64453C15.4863 7.60216 21.3667 12.1726 23.0869 18.625L23.2383 19.2383C24.6843 25.5695 22.3301 31.5383 17.8193 32.7412L17.5996 32.7949C13.1214 33.8045 7.46691 29.5531 5.55078 23.4248L5.37402 22.8154C3.62681 16.2608 5.9854 9.9409 10.6416 8.69922L10.8613 8.64453ZM27.4141 31.7158H29.2539L28.6904 20.4346H27.9775L27.4141 31.7158ZM16.6875 12.3506C14.6823 10.7737 12.6615 10.2827 11.1689 10.6807C9.65208 11.0854 8.31698 12.3661 7.52734 14.4824C6.73979 16.5936 6.57876 19.3732 7.35547 22.2871C8.11265 25.1272 9.80532 27.5419 11.7734 29.0898C13.7784 30.6665 15.7975 31.1564 17.29 30.7588C18.8072 30.3543 20.1428 29.0747 20.9326 26.958C21.7202 24.8468 21.8813 22.0673 21.1045 19.1533C20.3473 16.3133 18.6556 13.8985 16.6875 12.3506ZM14.3301 16.5908C16.4978 16.7006 18.2216 18.4935 18.2217 20.6885L18.2168 20.8994C18.107 23.0672 16.3142 24.791 14.1191 24.791L13.9082 24.7861C11.8105 24.6797 10.1277 22.9972 10.0215 20.8994L10.0166 20.6885C10.0167 18.4229 11.8536 16.5862 14.1191 16.5859L14.3301 16.5908ZM14.1191 18.6377C12.9865 18.6379 12.0684 19.5558 12.0684 20.6885C12.0684 21.8212 12.9865 22.74 14.1191 22.7402C15.252 22.7402 16.1709 21.8214 16.1709 20.6885C16.1708 19.5556 15.252 18.6377 14.1191 18.6377ZM28.334 7.61328C28.9003 7.61336 29.3593 8.07235 29.3594 8.63867V13.2539L29.3545 13.3594C29.3018 13.8763 28.8648 14.2792 28.334 14.2793C27.8031 14.2793 27.3662 13.8764 27.3135 13.3594L27.3086 13.2539V8.63867C27.3087 8.0723 27.7676 7.61328 28.334 7.61328Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },

  {
    key: 'breakfast_one_meal_of_choice',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M40 31.1387L39.9961 31.2881C39.9207 32.7729 38.7299 33.9637 37.2451 34.0391L37.0957 34.043H3.9043L3.75488 34.0391C2.26992 33.9638 1.07931 32.773 1.00391 31.2881L1 31.1387V28.6494H40V31.1387ZM2.65918 31.1387C2.65939 31.8259 3.21701 32.3838 3.9043 32.3838H37.0957C37.7829 32.3837 38.3396 31.8258 38.3398 31.1387V30.3086H2.65918V31.1387ZM20.708 5C22.6554 5.00035 24.2344 6.57989 24.2344 8.52734V9.21094C31.3808 10.9759 36.6803 17.4296 36.6807 25.1211V25.3291H37.9248L38.0098 25.334C38.428 25.3765 38.7547 25.7298 38.7549 26.1592C38.7549 26.5888 38.4281 26.9418 38.0098 26.9844L37.9248 26.9893H2.65918C2.2011 26.989 1.8291 26.6173 1.8291 26.1592C1.82934 25.7012 2.20125 25.3293 2.65918 25.3291H3.9043V25.1211C3.90465 17.1344 9.61793 10.4828 17.1807 9.0293V8.52734C17.1807 6.57967 18.7603 5 20.708 5ZM20.292 10.3926C12.1581 10.3928 5.56387 16.9872 5.56348 25.1211V25.3291H35.0215V25.1211C35.0211 16.9872 28.4259 10.3928 20.292 10.3926ZM20.708 6.66016C19.6769 6.66016 18.8408 7.49623 18.8408 8.52734V8.79785C19.3188 8.75591 19.8031 8.73341 20.292 8.7334L20.7158 8.73828C21.3448 8.75425 21.9648 8.80668 22.5742 8.8916V8.52734C22.5742 7.49644 21.7388 6.6605 20.708 6.66016Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },
  {
    key: 'full_meals',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M37.6738 8C38.4473 8.00013 39.0834 8.58851 39.1602 9.3418L39.168 9.49512V23.9717L39.167 23.9707V33.8506L39.1611 33.9688C39.1021 34.5482 38.6126 34.9999 38.0176 35L37.7461 34.9863C36.4978 34.8594 35.5049 33.8674 35.3779 32.6191L35.3643 32.3477V25.874C35.3643 24.8208 35.7926 23.8673 36.4844 23.1787C35.1251 22.2979 34.2247 20.7687 34.2246 19.0283V11.4492L34.2295 11.2715C34.3192 9.50828 35.7328 8.09437 37.4961 8.00488L37.6738 8ZM1.52148 16.5576C1.5217 17.5025 2.28751 18.2685 3.23242 18.2686C4.17731 18.2685 4.94314 17.5025 4.94336 16.5576V9.52246H6.46484V16.5576C6.46467 18.0114 5.50423 19.2402 4.18359 19.6465V33.8594L4.17969 33.9375C4.14062 34.3209 3.81652 34.6201 3.42285 34.6201C3.02916 34.6201 2.70509 34.3209 2.66602 33.9375L2.66211 33.8594V19.7383C1.14915 19.4688 0.00019051 18.1479 0 16.5576V9.52246H1.52148V16.5576ZM20.4531 8.00488C27.6448 8.18731 33.4189 14.0749 33.4189 21.3105L33.4141 21.6543C33.2317 28.8459 27.3449 34.62 20.1094 34.6201L19.7656 34.6152C12.6881 34.4359 6.9832 28.7318 6.80371 21.6543L6.7998 21.3105C6.7998 13.9599 12.7588 8.00098 20.1094 8.00098L20.4531 8.00488ZM37.6455 24.1768C37.18 24.5944 36.8858 25.1993 36.8857 25.874V32.3477C36.8858 32.8421 37.2036 33.261 37.6455 33.415V24.1768ZM20.1094 9.52246C13.5988 9.52246 8.32129 14.8 8.32129 21.3105C8.32135 27.821 13.5989 33.0986 20.1094 33.0986C26.6198 33.0985 31.8974 27.821 31.8975 21.3105C31.8975 14.8001 26.6198 9.52257 20.1094 9.52246ZM20.5986 11.8164C25.6215 12.0711 29.616 16.2244 29.6162 21.3105L29.6035 21.7998C29.3488 26.8228 25.1956 30.8173 20.1094 30.8174L19.6201 30.8047C14.7593 30.5581 10.8617 26.6607 10.6152 21.7998L10.6025 21.3105C10.6027 16.0604 14.8592 11.8039 20.1094 11.8037L20.5986 11.8164ZM20.1094 13.3252C15.6993 13.3254 12.1242 16.9005 12.124 21.3105C12.1241 25.7208 15.6992 29.2957 20.1094 29.2959C24.5197 29.2959 28.0947 25.7208 28.0947 21.3105C28.0945 16.9004 24.5196 13.3252 20.1094 13.3252ZM37.6475 9.52344C36.595 9.53755 35.7463 10.3934 35.7461 11.4492V19.0283C35.7462 20.372 36.5206 21.5347 37.6475 22.0947V9.52344ZM4.18359 16.748H2.66211V9.52246H4.18359V16.748Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },

  {
    key: 'tea_and_coffee_provided',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M29.1914 15.716H29.1904V17.1448H34.4287L34.6729 17.1574C35.8729 17.2798 36.8095 18.2932 36.8096 19.5256V25.716L36.8037 25.9612C36.6802 28.3958 34.7276 30.3483 32.293 30.4719L32.0479 30.4778H28.6973C27.1421 35.9734 22.0884 40.0002 16.0947 40.0002C8.97583 40 3.18337 34.3191 3.00391 27.2434L3 26.9055V14.2864H3.00098V13.8117H29.1914V15.716ZM4.9043 26.9055L4.91895 27.4807C5.21855 33.3933 10.1077 38.0957 16.0947 38.0959C22.2748 38.0959 27.2849 33.0856 27.2852 26.9055V15.716H4.9043V26.9055ZM29.1904 28.5735H32.0479C33.6255 28.5732 34.9051 27.2937 34.9053 25.716V19.5256C34.9052 19.2628 34.6915 19.0493 34.4287 19.049H29.1904V28.5735ZM9.76074 0.234594C10.1242 -0.0829874 10.6768 -0.0803473 11.0371 0.256078C11.4214 0.615171 11.4411 1.21844 11.082 1.60276L10.8574 1.8635C10.3854 2.46472 10.2677 3.01135 10.2969 3.54905C10.3344 4.2382 10.6141 4.96963 10.9951 5.88401C11.7018 7.58012 12.8046 9.91622 11.1865 12.4211C10.9011 12.8629 10.3119 12.9896 9.87012 12.7043C9.42856 12.4188 9.30155 11.8296 9.58691 11.3879C10.5831 9.8457 10.0176 8.49146 9.23633 6.61643C8.87338 5.74534 8.45366 4.71912 8.39551 3.65256C8.33348 2.5116 8.68221 1.38097 9.69043 0.301976L9.76074 0.234594ZM15.8018 0.234594C16.1652 -0.0829477 16.7178 -0.0803847 17.0781 0.256078C17.4624 0.615153 17.482 1.21844 17.123 1.60276L16.8984 1.8635C16.4265 2.46472 16.3087 3.01134 16.3379 3.54905C16.3755 4.23821 16.6552 4.96961 17.0361 5.88401C17.7428 7.58011 18.8455 9.91624 17.2275 12.4211C16.9421 12.8629 16.3529 12.9896 15.9111 12.7043C15.4695 12.4188 15.3425 11.8296 15.6279 11.3879C16.6241 9.84572 16.0586 8.49144 15.2773 6.61643C14.9144 5.74533 14.4947 4.71913 14.4365 3.65256C14.3745 2.5116 14.7232 1.38097 15.7314 0.301976L15.8018 0.234594ZM21.9932 0.234594C22.3567 -0.0827932 22.9093 -0.0805048 23.2695 0.256078C23.6537 0.615088 23.6732 1.21844 23.3145 1.60276L23.0898 1.8635C22.6179 2.46473 22.5001 3.01134 22.5293 3.54905C22.5669 4.23825 22.8465 4.96954 23.2275 5.88401C23.9342 7.58006 25.0368 9.91631 23.4189 12.4211C23.1336 12.8628 22.5443 12.9895 22.1025 12.7043C21.6607 12.4189 21.5339 11.8297 21.8193 11.3879C22.8153 9.8458 22.25 8.49135 21.4688 6.61643C21.1058 5.7453 20.6861 4.7192 20.6279 3.65256C20.5659 2.51159 20.9146 1.38098 21.9229 0.301976L21.9932 0.234594Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },

  {
    key: 'shared_kitchen_access',
    icon: (
      <svg
        width="28"
        height="40"
        viewBox="0 0 28 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M27.3027 25.0879C27.5497 25.0881 27.8033 25.074 27.9453 25.2999L27.999 25.4122C28.0046 29.5039 27.9648 33.5958 27.9951 37.6875C27.8234 38.0918 27.4602 38.0292 27.1445 38C26.7629 37.9648 26.6401 38.1027 26.6856 38.4688C26.7076 38.6459 26.6855 38.8316 26.6641 39.0108C26.6143 39.4265 26.3766 39.6159 25.9551 39.586C25.5484 39.557 25.373 39.3223 25.3594 38.9405C25.3483 38.6275 25.3478 38.3142 25.3428 38.001C25.2125 37.893 25.0574 37.9415 24.9141 37.9415C17.6567 37.9389 10.399 37.9387 3.14162 37.9415C3.01596 37.9414 2.87655 37.8889 2.76369 37.9952C2.73248 38.3715 2.70739 38.7485 2.66896 39.1241C2.63657 39.4405 2.44462 39.572 2.13283 39.584C1.80702 39.5964 1.53871 39.5337 1.42482 39.1866C1.4048 39.1252 1.39957 39.0573 1.3965 38.9922C1.38178 38.6814 1.47639 38.2958 1.32619 38.0811C1.18119 37.8739 0.768538 38.0556 0.48146 37.9991C0.106883 37.9253 -0.00147142 37.652 1.5039e-05 37.2999C0.00496711 36.1443 0.00196843 34.9887 0.00196816 33.8331C0.00176997 31.2078 0.000991612 28.5824 0.000991601 25.9571C0.000991601 25.2089 0.137387 25.0709 0.901382 25.0713C9.70181 25.0759 18.5023 25.0823 27.3027 25.0879ZM26.3535 26.3858C24.4404 26.4014 22.5264 26.4002 20.6133 26.3868C20.3004 26.3845 20.1957 26.4541 20.1973 26.7901C20.2122 29.9567 20.2117 33.1235 20.2022 36.2901C20.2013 36.6071 20.278 36.7069 20.6094 36.7042C21.5659 36.6958 22.5229 36.6915 23.4795 36.6915L26.3496 36.7032C26.6679 36.7056 26.7653 36.627 26.7617 36.2989C26.7446 34.7158 26.754 33.1322 26.7539 31.5489C26.7538 29.9656 26.7451 28.3821 26.7608 26.7989C26.7639 26.4796 26.6815 26.3832 26.3535 26.3858ZM10.334 34.1143C8.9148 34.135 7.49471 34.1222 6.07521 34.1221C4.62275 34.122 3.17025 34.1273 1.71779 34.1182C1.46394 34.1165 1.328 34.1494 1.33888 34.4571C1.36167 35.1 1.35411 35.7444 1.34084 36.3877C1.33608 36.6193 1.398 36.6979 1.64357 36.6973C4.58136 36.6902 7.51925 36.6922 10.457 36.7012C10.7007 36.702 10.7675 36.6214 10.7627 36.3897C10.7501 35.7793 10.7382 35.1671 10.7656 34.5577V34.5586C10.781 34.2171 10.6887 34.1092 10.334 34.1143ZM12.0791 26.3995C11.992 26.4977 12.0215 26.6184 12.0215 26.7305C12.02 29.9425 12.0199 33.1552 12.0215 36.3672C12.0215 36.4786 11.9891 36.6001 12.083 36.6954C14.3755 36.695 16.6685 36.6947 18.961 36.6944C19.1216 36.6106 19.0487 36.4599 19.0488 36.3418C19.0533 33.1476 19.0532 29.9531 19.0488 26.7588C19.0487 26.64 19.1203 26.4904 18.9629 26.4043C16.6683 26.4029 14.3737 26.401 12.0791 26.3995ZM21.6299 31.0176C21.9893 31.0165 22.228 31.1926 22.2559 31.5713C22.2727 31.8006 22.2588 32.0323 22.2588 32.2627H22.2549C22.2549 32.5093 22.2766 32.7583 22.25 33.002C22.2122 33.3484 21.9748 33.526 21.6445 33.5313C21.3114 33.5365 21.0527 33.354 21.0264 33.0167C20.9882 32.5261 20.9937 32.0289 21.0225 31.5372C21.0429 31.1886 21.2936 31.0187 21.6299 31.0176ZM17.3574 31.0196C17.7079 31.0185 17.9716 31.2031 17.9942 31.5713C18.0233 32.045 18.0243 32.5238 17.9912 32.9971C17.9675 33.3353 17.718 33.5238 17.3897 33.5293C17.0334 33.5353 16.7996 33.3329 16.7608 32.9708C16.7347 32.7276 16.7558 32.4794 16.7559 32.2334H16.7529V32.2325C16.7529 32.0192 16.7451 31.8056 16.7549 31.5928C16.7723 31.2142 16.9997 31.0207 17.3574 31.0196ZM10.375 30.2666C8.93963 30.2822 7.50395 30.2736 6.06837 30.2735C4.61622 30.2733 3.16399 30.2783 1.71193 30.2676C1.45278 30.2657 1.32759 30.3061 1.33888 30.6075C1.36197 31.2171 1.36206 31.8297 1.33986 32.4395C1.3289 32.7442 1.45965 32.7839 1.71877 32.7833C4.60672 32.776 7.49487 32.7742 10.3828 32.7852C10.698 32.7864 10.7734 32.6756 10.7637 32.3838H10.7647C10.7453 31.8069 10.7414 31.228 10.7656 30.6514C10.7787 30.3415 10.6778 30.2634 10.375 30.2666ZM10.4209 26.3965C7.50294 26.3931 4.58498 26.3954 1.66701 26.3907C1.45396 26.3903 1.33283 26.4179 1.34084 26.6788C1.36422 27.4359 1.36463 28.1937 1.37404 28.9512C1.46293 29.0593 1.5863 29.0206 1.6963 29.0206C4.57586 29.0226 7.45542 29.0231 10.335 29.0215C10.4468 29.0214 10.5678 29.0523 10.6651 28.9629C10.8736 28.1713 10.712 27.3656 10.752 26.5674C10.7637 26.3307 10.5523 26.3967 10.4209 26.3965ZM23.7852 0.411181C24.6504 -0.0135568 25.5717 -0.167837 26.4082 0.465869C27.2786 1.12514 27.4097 2.09792 27.1807 3.06645C26.7035 5.08309 25.7231 6.84079 24.1582 8.22856C23.4194 8.88371 22.5658 9.28945 21.126 9.05083C20.6631 8.93063 20.0725 9.3371 19.5059 9.9561C16.0083 13.777 12.4886 17.5779 8.97267 21.3819C8.74973 21.6231 8.51749 21.8654 8.25294 22.0567C7.71295 22.4471 7.01242 22.3592 6.59669 21.8848C6.18364 21.4133 6.17238 20.6914 6.62794 20.2178C7.26832 19.5524 7.93532 18.9111 8.60939 18.2793C11.6677 15.4129 14.6648 12.4827 17.6426 9.53325C17.7714 9.40562 17.8861 9.25845 18.0313 9.15337C18.9513 8.48832 19.3215 7.69872 18.8506 6.55474C18.6146 5.98116 18.8286 5.34524 19.1504 4.80278C20.2787 2.90136 21.7898 1.39098 23.7852 0.411181ZM15.6133 15.459C16.501 14.4333 16.4996 14.4265 17.4756 15.3389C19.1011 16.8583 20.7271 18.3771 22.3506 19.8985C22.7993 20.319 23.0566 20.8088 22.8496 21.4434C22.6841 21.9503 22.2244 22.2897 21.6983 22.2969L21.5703 22.292C21.278 22.2686 21.0256 22.1516 20.8154 21.9268C19.0822 20.0725 17.3481 18.2191 15.6123 16.3672C15.323 16.0586 15.3357 15.78 15.6133 15.459ZM4.3672 0.241259C4.64904 -0.075958 5.04518 -0.080556 5.42384 0.230517C5.46179 0.261713 5.4945 0.299114 5.53029 0.333056C6.44183 1.19476 7.3557 2.05462 8.26466 2.91899C8.81486 3.44224 9.36841 3.96338 9.90236 4.50298C10.4616 5.06827 10.7561 5.80746 10.7569 6.50005V6.7061C10.7403 7.17842 10.6033 7.59171 10.3613 7.95317C10.1522 8.26546 10.2477 8.42399 10.4805 8.64263C11.443 9.54665 12.3905 10.4669 13.3418 11.3829C13.8703 11.8917 13.8688 11.9952 13.3408 12.5401C12.8546 13.0418 12.6575 13.0495 12.1572 12.5518C11.1975 11.597 10.2443 10.6352 9.28908 9.67583C9.15315 9.5393 9.07254 9.41008 8.81252 9.5606C7.45633 10.3458 6.22 10.2176 5.06447 9.12017C3.60397 7.7329 2.28748 6.20387 0.913101 4.73247C0.609793 4.40776 0.626601 4.01112 0.919937 3.76763C1.26173 3.484 1.5811 3.5568 1.88087 3.85747C2.98829 4.96815 4.09891 6.07563 5.209 7.18364C5.47013 7.44427 5.75317 7.50801 6.02638 7.21587C6.27783 6.94686 6.28108 6.65695 6.0215 6.37993C5.96519 6.31977 5.90654 6.26176 5.84865 6.20317C4.86201 5.20514 3.8759 4.20648 2.88966 3.20806C2.78542 3.10243 2.66999 3.00363 2.58498 2.88384C2.36433 2.57259 2.38605 2.259 2.64845 1.9854C2.92388 1.6984 3.23303 1.7177 3.53419 1.9522C3.65052 2.04288 3.75401 2.15074 3.85939 2.25493C4.84583 3.22987 5.83146 4.20625 6.8174 5.18169C6.89941 5.26284 6.97769 5.34849 7.06544 5.4229C7.36076 5.67332 7.68175 5.69274 7.95021 5.41704C8.22684 5.13295 8.10457 4.85292 7.84962 4.60063C6.72296 3.48577 5.59865 2.36853 4.47853 1.24712C4.14232 0.910507 4.10956 0.531575 4.3672 0.241259Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },

  {
    key: 'traditional_cooking',
    icon: (
      <svg
        width="29"
        height="40"
        viewBox="0 0 29 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clip-path="url(#clip0_1268_6359)">
          <path
            d="M13.4309 37.3033C13.4309 37.7999 13.8446 38.2021 14.3554 38.2021C14.8657 38.2021 15.2798 37.7994 15.2798 37.3033V33.7078H17.1287V37.3033C17.1287 38.7927 15.8872 39.9999 14.3554 39.9999C12.8236 39.9999 11.582 38.7927 11.582 37.3033V33.7078H13.4309V37.3033Z"
            fill="#FE7755"
          />
          <path d="M15.2799 7.19092H14.3555V8.98867H15.2799V7.19092Z" fill="#FE7755" />
          <path
            d="M28.2162 32.6274L28.108 32.4912L25.9564 29.8908C26.6765 28.9384 27.0523 27.7321 26.915 26.4562L26.8979 26.3173L25.4489 15.6719V15.1065L25.6116 15.1717C26.8526 12.2211 24.6206 8.98876 21.3425 8.98876H17.1293V7.19101H18.0537V3.14607C18.0533 1.40854 16.6046 0 14.8177 0C13.0307 0 11.5821 1.40854 11.5821 3.14607V7.19101H12.5066V8.98876H8.48474C5.5057 8.98876 3.46729 11.742 4.18651 14.3888V15.6571L2.43653 26.1766C2.24609 27.3204 2.47443 28.4238 3.00229 29.3519L0.899629 31.8935L0.790544 32.0315C-0.301231 33.4751 -0.261942 35.4634 0.907025 36.8674L1.0591 37.0396C2.61818 38.7115 5.31157 38.7744 6.95108 37.1766L7.11147 37.0117L11.7906 31.9101H16.9675L21.9013 37.5043L22.0589 37.6733C23.6725 39.3061 26.3826 39.2683 27.9481 37.5919L28.1011 37.4189C29.2594 36.0274 29.2983 34.0575 28.2167 32.627L28.2162 32.6274ZM13.431 3.14607C13.431 2.40135 14.0518 1.79775 14.8177 1.79775C15.5836 1.79775 16.2044 2.40135 16.2044 3.14607V5.39326H13.431V3.14607ZM8.48474 10.7865H21.342C23.2662 10.7865 24.5873 12.6445 23.9415 14.382H6.14635C5.33283 12.7187 6.57806 10.7865 8.48474 10.7865ZM5.73127 35.8148L5.64345 35.9052C4.74812 36.7771 3.27824 36.7429 2.42682 35.8301L2.34362 35.7362C1.70529 34.9694 1.68403 33.8845 2.27983 33.0962L2.339 33.0207L4.20361 30.7667C5.07953 31.4769 6.20828 31.9097 7.45537 31.9097H9.31259L5.73081 35.8144L5.73127 35.8148ZM7.45583 30.1124C5.46272 30.1124 3.94338 28.3771 4.26185 26.4638L5.97255 16.1798H23.6531L25.0652 26.5533L25.0842 26.7299C25.227 28.5416 23.752 30.1124 21.8578 30.1124H7.45583ZM26.6636 36.2881L26.5809 36.382C25.7327 37.2903 24.2642 37.3101 23.3901 36.4261L23.3041 36.3344L19.4034 31.9101H21.8578C22.8761 31.9101 23.8167 31.6198 24.6053 31.1258L26.6677 33.618L26.726 33.6921C27.3121 34.4674 27.2913 35.5344 26.6636 36.2881Z"
            fill="#FE7755"
          />
        </g>
        <defs>
          <clipPath id="clip0_1268_6359">
            <rect width="29" height="40" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
  },

  {
    key: 'electricity_full_time',
    icon: (
      <svg
        width="39"
        height="33"
        viewBox="0 0 39 33"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M19.2646 8.83011C19.52 8.38533 20.0932 8.22868 20.5451 8.48001C20.997 8.73139 21.1566 9.29558 20.9013 9.74036L15.6901 18.8145L22.8699 17.6318L22.885 17.63C22.9025 17.6274 22.9201 17.6253 22.9378 17.6237L22.9653 17.621C22.9699 17.6207 22.9745 17.6207 22.9791 17.6205C22.9941 17.6198 23.009 17.6201 23.0241 17.6201C23.0391 17.62 23.0541 17.6198 23.069 17.6205C23.0828 17.6211 23.0966 17.6224 23.1104 17.6237C23.1264 17.6251 23.1423 17.6264 23.1581 17.6286C23.1724 17.6307 23.1865 17.6332 23.2008 17.6359C23.2168 17.6389 23.2327 17.6424 23.2485 17.6463C23.2603 17.6491 23.2721 17.652 23.2838 17.6553C23.302 17.6604 23.3198 17.6663 23.3376 17.6725C23.3484 17.6763 23.3593 17.68 23.3701 17.6842C23.3866 17.6906 23.4028 17.6977 23.4188 17.705C23.4275 17.709 23.4367 17.7125 23.4454 17.7167C23.4496 17.7188 23.4536 17.7214 23.4578 17.7235C23.4713 17.7304 23.4842 17.7381 23.4973 17.7456C23.5094 17.7526 23.5218 17.759 23.5335 17.7664C23.5431 17.7725 23.5522 17.7794 23.5615 17.7859C23.5769 17.7964 23.5924 17.8066 23.607 17.8179C23.6295 17.8354 23.6513 17.854 23.6721 17.8735C23.6816 17.8823 23.6902 17.8919 23.6992 17.901C23.7111 17.913 23.7229 17.925 23.7341 17.9376C23.7418 17.9463 23.7488 17.9554 23.7561 17.9643C23.7679 17.9786 23.7796 17.993 23.7906 18.0081C23.7957 18.0152 23.8003 18.0226 23.8053 18.0298C23.8172 18.0473 23.8289 18.0651 23.8397 18.0835C23.8436 18.0902 23.847 18.0971 23.8507 18.1039C23.8605 18.1218 23.87 18.1399 23.8787 18.1585C23.8832 18.1682 23.8874 18.1781 23.8915 18.1879C23.8988 18.205 23.9055 18.2222 23.9117 18.2398C23.916 18.2518 23.9199 18.2639 23.9237 18.276C23.9281 18.2905 23.9323 18.305 23.9361 18.3198C23.9399 18.3348 23.9431 18.3498 23.9462 18.365C23.9479 18.3738 23.9506 18.3827 23.9521 18.3916C23.953 18.3967 23.9532 18.4019 23.954 18.407C23.9564 18.4226 23.9579 18.4383 23.9595 18.454C23.9609 18.4682 23.9619 18.4823 23.9627 18.4964C23.9634 18.5097 23.9639 18.5229 23.9641 18.5362C23.9642 18.5536 23.964 18.5709 23.9631 18.5881C23.9625 18.6012 23.9616 18.6143 23.9604 18.6274C23.9591 18.642 23.9574 18.6564 23.9553 18.6708C23.9533 18.6851 23.9512 18.6994 23.9485 18.7137C23.9453 18.7302 23.9414 18.7463 23.9374 18.7625C23.9343 18.7753 23.9311 18.7882 23.9273 18.8009C23.9232 18.8148 23.9183 18.8284 23.9136 18.842C23.9085 18.8566 23.9038 18.8714 23.898 18.8858C23.8924 18.8996 23.8859 18.913 23.8796 18.9265C23.8748 18.9369 23.8711 18.9478 23.8658 18.9581L19.1659 28.2093L19.1182 28.292C18.8631 28.688 18.3399 28.8373 17.9046 28.6231C17.4694 28.4089 17.2768 27.9073 17.4456 27.4689L17.4842 27.3822L21.3575 19.7568L14.2502 20.9282C13.9572 20.9764 13.6736 20.885 13.4681 20.7041C13.0762 20.4586 12.9267 19.9687 13.1115 19.554L13.1547 19.4695L19.2646 8.83011Z"
          fill="#FE7755"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M18.2851 0.683428C18.8305 -0.227802 20.1696 -0.227817 20.7149 0.683428L38.8028 30.908C39.3391 31.8042 38.7198 32.9275 37.6889 32.9968L37.5879 33H1.41215L1.31117 32.9968C0.280106 32.9276 -0.339117 31.8042 0.197233 30.908L18.2851 0.683428ZM2.23418 31.1497H36.7659L19.5 2.29885L2.23418 31.1497Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },

  {
    key: 'Electricity_limited_hours',
    icon: (
      <svg
        width="39"
        height="39"
        viewBox="0 0 39 39"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M18.9868 29.7632C19.5183 29.7632 19.9552 30.1671 20.0076 30.6847L20.0132 30.7895V32.3289C20.0132 32.8958 19.5537 33.3553 18.9868 33.3553C18.42 33.3553 17.9605 32.8958 17.9605 32.3289V30.7895L17.966 30.6847C18.0185 30.1671 18.4554 29.7632 18.9868 29.7632Z"
          fill="#FE7755"
        />
        <path
          d="M9.10254 26.4011C9.50564 26.0725 10.1 26.0961 10.4756 26.4717C10.8513 26.8474 10.8749 27.4417 10.5463 27.8448L10.4756 27.923L8.93616 29.4625C8.53536 29.8633 7.88569 29.8633 7.48489 29.4625C7.08409 29.0617 7.08409 28.412 7.48489 28.0112L9.02436 26.4717L9.10254 26.4011Z"
          fill="#FE7755"
        />
        <path
          d="M26.4717 26.4717C26.8474 26.0961 27.4417 26.0725 27.8448 26.4011L27.923 26.4717L29.4625 28.0112C29.8633 28.412 29.8633 29.0617 29.4625 29.4625C29.0617 29.8633 28.412 29.8633 28.0112 29.4625L26.4717 27.923L26.4011 27.8448C26.0725 27.4417 26.0961 26.8474 26.4717 26.4717Z"
          fill="#FE7755"
        />
        <path
          d="M19.5 7.69737C20.0668 7.69737 20.5263 8.15687 20.5263 8.72368V18.1038L25.7155 24.369L25.7782 24.4532C26.0679 24.8853 25.989 25.4753 25.5797 25.8142C25.1705 26.1531 24.5767 26.1206 24.2061 25.7556L24.135 25.6784L18.7097 19.1282C18.688 19.1019 18.6672 19.0748 18.6481 19.0465C18.6413 19.0364 18.6359 19.0256 18.6295 19.0154C18.6007 18.9691 18.5747 18.9209 18.5534 18.8701C18.5448 18.8496 18.5375 18.8288 18.5303 18.8079C18.5265 18.7969 18.5222 18.7861 18.5188 18.7749C18.4889 18.6774 18.4739 18.5767 18.4737 18.4762V8.72368C18.4737 8.15687 18.9332 7.69737 19.5 7.69737Z"
          fill="#FE7755"
        />
        <path
          d="M6.77579 17.966C7.2934 18.0185 7.69737 18.4554 7.69737 18.9868C7.69737 19.5183 7.2934 19.9552 6.77579 20.0076L6.67105 20.0132H5.13158C4.56476 20.0132 4.10526 19.5537 4.10526 18.9868C4.10526 18.42 4.56476 17.9605 5.13158 17.9605H6.67105L6.77579 17.966Z"
          fill="#FE7755"
        />
        <path
          d="M32.9468 17.966C33.4645 18.0185 33.8684 18.4554 33.8684 18.9868C33.8684 19.5183 33.4645 19.9552 32.9468 20.0076L32.8421 20.0132H31.3026C30.7358 20.0132 30.2763 19.5537 30.2763 18.9868C30.2763 18.42 30.7358 17.9605 31.3026 17.9605H32.8421L32.9468 17.966Z"
          fill="#FE7755"
        />
        <path
          d="M7.48489 8.5112C7.88569 8.1104 8.53536 8.1104 8.93616 8.5112L10.4756 10.0507L10.5463 10.1289C10.8749 10.532 10.8513 11.1263 10.4756 11.502C10.1 11.8776 9.50564 11.9012 9.10254 11.5726L9.02436 11.502L7.48489 9.96248C7.08409 9.56168 7.08409 8.91201 7.48489 8.5112Z"
          fill="#FE7755"
        />
        <path
          d="M28.0112 8.5112C28.412 8.1104 29.0617 8.1104 29.4625 8.5112C29.8633 8.91201 29.8633 9.56168 29.4625 9.96248L27.923 11.502L27.8448 11.5726C27.4417 11.9012 26.8474 11.8776 26.4717 11.502C26.0961 11.1263 26.0725 10.532 26.4011 10.1289L26.4717 10.0507L28.0112 8.5112Z"
          fill="#FE7755"
        />
        <path
          d="M18.9868 3.07895C19.5183 3.07895 19.9552 3.48292 20.0076 4.00053L20.0132 4.10526V5.64474C20.0132 6.21156 19.5537 6.67105 18.9868 6.67105C18.42 6.67105 17.9605 6.21156 17.9605 5.64474V4.10526L17.966 4.00053C18.0185 3.48292 18.4554 3.07895 18.9868 3.07895Z"
          fill="#FE7755"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M19.5 0C30.2696 0 39 8.73045 39 19.5C39 30.2696 30.2696 39 19.5 39C8.73045 39 0 30.2696 0 19.5C0 8.73045 8.73045 0 19.5 0ZM19.5 2.05263C9.86409 2.05263 2.05263 9.86409 2.05263 19.5C2.05263 29.1359 9.86409 36.9474 19.5 36.9474C29.1359 36.9474 36.9474 29.1359 36.9474 19.5C36.9474 9.86409 29.1359 2.05263 19.5 2.05263Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },

  {
    key: 'solar_power',
    icon: (
      <svg
        width="35"
        height="39"
        viewBox="0 0 35 39"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M17.6277 7.05643C20.0133 7.11466 22.3165 7.93531 24.1943 9.39629C26.072 10.8573 27.4243 12.8808 28.0499 15.1655C28.6755 17.4501 28.5409 19.8741 27.6664 22.0769C27.0317 23.6755 25.7086 25.0909 24.2555 26.2242V31.9468C24.2555 35.8421 21.0725 38.9999 17.1462 39C13.2199 39 10.0369 35.8422 10.0369 31.9468V26.2145C8.65164 24.8822 7.40507 23.1771 6.85717 21.5696C6.09284 19.3269 6.07836 16.8991 6.81633 14.6477C7.55438 12.3962 9.00547 10.4411 10.9534 9.07337C12.9012 7.70574 15.2421 6.99825 17.6277 7.05643ZM11.7738 32.7766C12.1761 35.3614 14.4281 37.3404 17.1462 37.3404C19.8642 37.3404 22.1163 35.3614 22.5186 32.7766H11.7738ZM17.5873 8.69494C15.5573 8.64543 13.5652 9.24751 11.9078 10.4112C10.2503 11.575 9.01544 13.2387 8.38742 15.1546C7.75946 17.0703 7.77174 19.1361 8.42213 21.0445C9.07254 22.9529 10.0387 24.3713 11.7097 25.516V31.117H15.0552V24.236L15.0597 24.1513C15.0605 24.1439 15.0616 24.1365 15.0626 24.1291C14.8543 23.9553 14.6649 23.755 14.4982 23.5286C13.9551 22.7913 13.7169 21.876 13.8321 20.97C13.9474 20.064 14.4075 19.2359 15.1181 18.6556C15.8289 18.0754 16.7367 17.7868 17.6551 17.8489C18.5735 17.9112 19.4334 18.3195 20.0581 18.9903C20.6827 19.6611 21.0253 20.5437 21.0153 21.457C21.0053 22.3701 20.6437 23.245 20.0046 23.9022C19.7507 24.1632 19.5008 24.367 19.2347 24.5217L19.2372 24.5662V31.117H22.5827V25.3085C24.3084 24.2468 25.3851 23.3508 26.1292 21.4764C26.8733 19.602 26.9879 17.5391 26.4555 15.595C25.9232 13.6509 24.7723 11.9291 23.1745 10.6859C21.5767 9.44287 19.6171 8.74452 17.5873 8.69494ZM17.5779 18.9753C16.9494 18.9327 16.3283 19.1304 15.8418 19.5276C15.3554 19.9246 15.0403 20.4909 14.9613 21.111C14.8824 21.7311 15.0454 22.3579 15.4171 22.8625C15.609 23.123 15.7869 23.294 15.9994 23.4139C16 23.5 16.3876 23.5432 16.5671 23.7482C16.7623 23.9711 16.728 24.0017 16.728 24.1582C16.728 24.1706 16.7263 24.1829 16.7256 24.1951L16.728 24.236V31.117H17.5644V24.5662L17.5689 24.4816C17.5918 24.2576 17.7045 24.0602 17.8707 23.9249C17.9766 23.7443 18.171 23.613 18.3947 23.5667C18.6816 23.5074 18.9235 23.387 19.1853 23.1178C19.6227 22.668 19.8703 22.0694 19.8771 21.4444C19.8839 20.8194 19.6496 20.2151 19.2221 19.7561C18.7946 19.2972 18.2063 19.0179 17.5779 18.9753Z"
          fill="#FE7755"
        />
        <path
          d="M34.0169 13.2073C34.4342 13.1332 34.8487 13.3833 34.9676 13.7964C35.0866 14.2096 34.8677 14.639 34.4739 14.7948L34.393 14.8223L31.1761 15.7339C30.732 15.8598 30.269 15.6048 30.1421 15.1643C30.0152 14.7236 30.2726 14.2643 30.7167 14.1384L33.9336 13.2264L34.0169 13.2073Z"
          fill="#FE7755"
        />
        <path
          d="M0.0251738 13.905C0.137251 13.4605 0.591171 13.1903 1.03921 13.3013L4.28512 14.1064L4.3668 14.1311C4.76564 14.2738 4.99866 14.696 4.89362 15.1128C4.78853 15.5296 4.38281 15.793 3.9633 15.7327L3.87917 15.7165L0.633679 14.9115C0.185546 14.8003 -0.0868593 14.3496 0.0251738 13.905Z"
          fill="#FE7755"
        />
        <path
          d="M6.0996 3.56225C6.42623 3.2382 6.95609 3.2382 7.28272 3.56225L9.64813 5.90899L9.7053 5.9722C9.97334 6.29812 9.95438 6.77894 9.64813 7.08277C9.34191 7.38644 8.8576 7.40527 8.52913 7.13949L8.46542 7.08277L6.0996 4.73562C5.77315 4.4116 5.77315 3.88627 6.0996 3.56225Z"
          fill="#FE7755"
        />
        <path
          d="M27.4275 3.56225C27.7541 3.2382 28.284 3.2382 28.6106 3.56225C28.9371 3.88628 28.9371 4.41159 28.6106 4.73562L26.2448 7.08277L26.1815 7.13949C25.853 7.40539 25.3683 7.38653 25.0621 7.08277C24.7558 6.77894 24.7369 6.29812 25.0049 5.9722L25.0621 5.90899L27.4275 3.56225Z"
          fill="#FE7755"
        />
        <path
          d="M17.1462 0C17.6081 8.21029e-05 17.9826 0.371559 17.9826 0.829787V4.14894L17.9781 4.23362C17.9353 4.65206 17.5792 4.97865 17.1462 4.97872C16.7131 4.97872 16.3571 4.6521 16.3143 4.23362L16.3098 4.14894V0.829787C16.3098 0.371508 16.6843 0 17.1462 0Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },

  {
    key: 'lighting_in_room',
    icon: (
      <svg
        width="33"
        height="39"
        viewBox="0 0 33 39"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M16.72 0C17.206 2.11574e-08 17.6 0.39238 17.6 0.876405V14.4607H17.9068L18.1178 14.4658C20.2734 14.5747 21.9882 16.3449 21.9991 18.5162C28.2089 19.3381 32.9999 24.6318 33 31.0409L32.9953 31.138C32.9498 31.584 32.5936 31.9387 32.1458 31.9841L32.0482 31.9888H23.3136C23.315 31.9981 23.3165 32.0075 23.3174 32.017L23.32 32.0705V32.427C23.32 36.0572 20.3651 39 16.72 39C13.0749 39 10.12 36.0572 10.12 32.427V32.0705L10.1226 32.017C10.1235 32.0075 10.125 31.9981 10.1264 31.9888H0.951758L0.854219 31.9841C0.406418 31.9387 0.0502464 31.584 0.00472656 31.138L0 31.0409C6.59717e-05 24.4809 5.01929 19.0886 11.4417 18.4648C11.4792 16.317 13.1836 14.5739 15.3222 14.4658L15.5332 14.4607H15.84V0.876405C15.84 0.39238 16.234 -4.53396e-10 16.72 0ZM11.9612 33.3034C12.3751 35.5469 14.3475 37.2472 16.72 37.2472C19.0925 37.2472 21.0649 35.5469 21.4788 33.3034H11.9612ZM12.6882 20.1573C6.92462 20.1574 2.20335 24.601 1.78965 30.236H31.2104C30.7967 24.601 26.0754 20.1574 20.3118 20.1573H12.6882ZM15.5332 16.2135C14.7942 16.2135 14.1358 16.556 13.7083 17.0899C16.0745 18.039 17.3973 17.7904 19.7317 17.0899C19.3042 16.556 18.6458 16.2135 17.9068 16.2135H15.5332Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },

  {
    key: 'phone_charging_point',
    icon: (
      <svg
        width="26"
        height="39"
        viewBox="0 0 26 39"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M15.3541 33.1335C15.7704 33.1764 16.0952 33.5334 16.0952 33.9677C16.0952 34.402 15.7704 34.7591 15.3541 34.8019L15.2698 34.8064H11.5556C11.0997 34.8064 10.7302 34.4309 10.7302 33.9677C10.7302 33.5045 11.0997 33.129 11.5556 33.129H15.2698L15.3541 33.1335Z"
          fill="#FE7755"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M15.7668 0.00450479C16.1831 0.0473891 16.5079 0.404405 16.5079 0.83871C16.5079 1.27301 16.1831 1.63003 15.7668 1.67291L15.6825 1.67742H1.65079V30.1935H24.3492V1.67742H19.8095C19.3537 1.67742 18.9841 1.30192 18.9841 0.83871C18.9841 0.375503 19.3537 -8.6779e-10 19.8095 0H25.1746L25.2588 0.00450479C25.6751 0.0473891 26 0.404405 26 0.83871V37.7419C26 38.4367 25.4457 39 24.7619 39H1.2381C0.554316 39 0 38.4367 0 37.7419V0.83871C0 0.375503 0.369543 0 0.825397 0H15.6825L15.7668 0.00450479ZM1.65079 37.3226H24.3492V31.871H1.65079V37.3226Z"
          fill="#FE7755"
        />
        <path
          d="M13.7198 8.40471C13.9381 7.99812 14.4396 7.84833 14.8398 8.07012C15.2399 8.29196 15.3874 8.80158 15.1691 9.2082L10.9357 17.094L15.6277 16.7762C15.6296 16.7761 15.6315 16.7759 15.6334 16.7758C15.6525 16.7747 15.6717 16.7748 15.691 16.775C15.6995 16.7751 15.708 16.7747 15.7164 16.775C15.7386 16.7759 15.7607 16.778 15.7829 16.7807C15.7873 16.7813 15.7918 16.7814 15.7962 16.782C15.8166 16.7848 15.8368 16.789 15.857 16.7934C15.8639 16.7949 15.8708 16.7959 15.8776 16.7975C15.8798 16.7981 15.8819 16.7986 15.8841 16.7992L15.9526 16.8201L15.9622 16.8237C15.9686 16.8261 15.9748 16.829 15.9812 16.8315C15.9965 16.8376 16.0119 16.8434 16.0267 16.8504L16.0388 16.8561C16.0466 16.8599 16.054 16.8647 16.0618 16.8688C16.0767 16.8767 16.0917 16.8842 16.1061 16.893C16.1084 16.8943 16.1107 16.8956 16.113 16.8971C16.1207 16.9019 16.128 16.9076 16.1355 16.9126C16.1482 16.9211 16.1608 16.9297 16.173 16.9388C16.1833 16.9465 16.193 16.9549 16.2028 16.963C16.2137 16.972 16.2247 16.9809 16.2351 16.9904C16.2459 17.0004 16.2563 17.0107 16.2665 17.0211C16.2746 17.0293 16.2826 17.0376 16.2903 17.0461C16.3023 17.0594 16.3135 17.0732 16.3246 17.0871C16.3297 17.0936 16.3349 17.1001 16.3399 17.1067C16.3497 17.1199 16.3591 17.1332 16.3681 17.1469C16.3749 17.1572 16.3815 17.1677 16.3878 17.1784C16.3947 17.1899 16.4013 17.2014 16.4076 17.2132C16.4141 17.2253 16.4202 17.2376 16.4261 17.2501C16.4319 17.2622 16.4371 17.2745 16.4422 17.2869C16.4474 17.2994 16.4526 17.3118 16.4572 17.3246C16.4624 17.3392 16.4668 17.354 16.4713 17.3688C16.4743 17.3791 16.4774 17.3895 16.4801 17.4C16.4844 17.4165 16.4877 17.4332 16.491 17.4499C16.493 17.4603 16.4954 17.4705 16.4971 17.481C16.5001 17.5002 16.5018 17.5194 16.5035 17.5388C16.504 17.5449 16.5055 17.551 16.5059 17.5572C16.506 17.5591 16.5058 17.561 16.5059 17.5629C16.5071 17.5823 16.5073 17.6017 16.5071 17.6211C16.5071 17.6299 16.5075 17.6386 16.5071 17.6473C16.5053 17.6925 16.4993 17.7376 16.4902 17.7824C16.4883 17.792 16.4868 17.8017 16.4846 17.8111C16.4814 17.8243 16.4771 17.8373 16.4733 17.8504C16.469 17.865 16.465 17.8796 16.46 17.8938C16.4573 17.9014 16.454 17.9088 16.4511 17.9164C16.4443 17.934 16.4377 17.9517 16.4298 17.9688C16.4251 17.9788 16.4195 17.9884 16.4144 17.9983C16.4067 18.0134 16.3993 18.0287 16.3907 18.0433C16.3893 18.0456 16.3884 18.048 16.387 18.0503L11.8473 25.5987L11.7994 25.6695C11.5463 26.0079 11.077 26.1031 10.7124 25.8767C10.3479 25.6503 10.2176 25.1829 10.3985 24.7997L10.4388 24.7239L14.147 18.5573L9.54728 18.8685C9.49503 18.8721 9.44233 18.8707 9.3897 18.864C9.37858 18.8626 9.36763 18.8605 9.35665 18.8587C9.31771 18.8521 9.27886 18.8433 9.24058 18.8308C9.16614 18.8066 9.09608 18.7721 9.03221 18.7285C9.02218 18.7216 9.01284 18.7139 9.00319 18.7067C8.99252 18.6988 8.98163 18.6911 8.97135 18.6826C8.96282 18.6756 8.95497 18.6678 8.94677 18.6605C8.93549 18.6504 8.92405 18.6405 8.91332 18.6298C8.90639 18.6228 8.89982 18.6156 8.89317 18.6085C8.88316 18.5977 8.8732 18.587 8.86375 18.5757C8.85242 18.5622 8.8419 18.5481 8.8315 18.5339C8.82783 18.5289 8.82378 18.5243 8.82022 18.5192C8.81079 18.5057 8.80222 18.4918 8.79362 18.4778C8.78781 18.4684 8.78174 18.4592 8.77629 18.4496C8.7719 18.4418 8.76794 18.4337 8.7638 18.4258C8.74594 18.3917 8.72983 18.3565 8.71664 18.3197L8.71261 18.3075C8.71095 18.3026 8.70975 18.2976 8.70818 18.2927C8.69493 18.2514 8.68403 18.2089 8.67715 18.1649C8.66508 18.0887 8.66317 18.0106 8.67231 17.9327C8.6848 17.8255 8.71721 17.7237 8.76621 17.6322L8.76742 17.6305L13.7198 8.40471Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },

  {
    key: 'fan_provided',
    icon: (
      <svg
        width="35"
        height="39"
        viewBox="0 0 35 39"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M27.5085 37.1909C27.961 37.2374 28.3139 37.6235 28.3139 38.093C28.3139 38.5626 27.961 38.9486 27.5085 38.9951L27.4165 39H7.17949C6.68385 39 6.28205 38.5939 6.28205 38.093C6.28205 37.5921 6.68385 37.186 7.17949 37.186H27.4165L27.5085 37.1909Z"
          fill="#FE7755"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M17.5 0C27.165 0 35 7.91831 35 17.686C35 27.4538 27.165 35.3721 17.5 35.3721C7.83502 35.3721 0 27.4538 0 17.686C0 7.91831 7.83502 0 17.5 0ZM15.3239 23.2714C15.3078 23.2898 15.2834 23.3174 15.252 23.3538C15.1893 23.4265 15.0978 23.5336 14.9847 23.6686C14.7581 23.9392 14.4458 24.3209 14.1048 24.7625C13.4117 25.6603 12.6412 26.7546 12.2048 27.6637C11.7684 28.5727 11.3947 29.8612 11.1259 30.967C11.0244 31.3849 10.9408 31.7676 10.8753 32.0799C12.8883 33.0278 15.1328 33.5581 17.5 33.5581C19.4869 33.5581 21.3872 33.184 23.137 32.5037C23.0766 32.4412 23.0145 32.3777 22.9521 32.3124C22.1059 31.4256 21.0571 30.2438 20.3759 29.1672C19.6947 28.0905 19.0733 26.6323 18.6319 25.4839C18.4082 24.9019 18.2247 24.3841 18.0973 24.0114C18.0335 23.8249 17.9834 23.6736 17.9492 23.569C17.9464 23.5604 17.9439 23.5521 17.9413 23.5442C17.7227 23.5683 17.5006 23.5814 17.2756 23.5814C16.5963 23.5814 15.9432 23.4683 15.3335 23.2599C15.3306 23.2631 15.3276 23.2672 15.3239 23.2714ZM21.549 21.7976C21.0147 22.3351 20.3819 22.7725 19.68 23.0796C19.7098 23.1696 19.748 23.284 19.794 23.4184C19.9159 23.7749 20.0912 24.271 20.3049 24.8272C20.7386 25.9554 21.308 27.2727 21.8881 28.1898C22.4684 29.1068 23.4129 30.1816 24.2444 31.0529C24.4703 31.2897 24.6851 31.5078 24.8771 31.6999C28.4016 29.8009 31.1252 26.5848 32.4041 22.7005C32.1107 22.7486 31.7752 22.8014 31.4133 22.8516C30.2057 23.0189 28.643 23.1772 27.381 23.0978C26.119 23.0184 24.5875 22.6654 23.4096 22.348C22.8126 22.1871 22.2905 22.0308 21.9175 21.9149C21.7703 21.8692 21.6459 21.8288 21.549 21.7976ZM12.1837 20.8875C11.8331 20.8953 11.3435 20.9125 10.7903 20.9473C9.66568 21.0181 8.34424 21.1606 7.3815 21.4331C6.41878 21.7056 5.21675 22.2774 4.21944 22.8073C3.72892 23.0679 3.30188 23.3102 2.99817 23.4875C2.96383 23.5076 2.93109 23.5272 2.90002 23.5455C4.15058 26.7254 6.38931 29.399 9.23246 31.1818C9.27776 30.9796 9.32792 30.7622 9.3832 30.5347C9.65725 29.4071 10.0665 27.9624 10.59 26.8718C11.1136 25.7813 11.983 24.5619 12.6899 23.6465C13.0488 23.1816 13.3766 22.7813 13.6149 22.4968C13.6436 22.4626 13.6713 22.4303 13.6973 22.3994C13.135 21.9827 12.6464 21.4705 12.2552 20.8862C12.2321 20.8866 12.2082 20.8869 12.1837 20.8875ZM6.85872 14.7658C5.86706 14.637 4.53909 14.6895 3.41622 14.784C2.86514 14.8304 2.37944 14.8849 2.0315 14.9284C1.87624 15.824 1.79487 16.7454 1.79487 17.686C1.79487 19.1047 1.98005 20.4796 2.32553 21.7883C2.61713 21.6208 2.97921 21.4169 3.38379 21.2019C4.40075 20.6616 5.74238 20.0129 6.89729 19.686C8.05223 19.3591 9.53222 19.209 10.679 19.1369C10.9491 19.1199 11.2045 19.1072 11.4375 19.0974C11.2946 18.576 11.2179 18.0267 11.2179 17.4593C11.2179 16.9952 11.2692 16.5431 11.3661 16.1086C11.0526 15.984 10.644 15.8266 10.1847 15.6631C9.12197 15.2847 7.85036 14.8947 6.85872 14.7658ZM17.2756 13.1512C14.9214 13.1512 13.0128 15.08 13.0128 17.4593C13.0128 19.8386 14.9214 21.7674 17.2756 21.7674C19.6299 21.7674 21.5385 19.8386 21.5385 17.4593C21.5385 15.08 19.6299 13.1512 17.2756 13.1512ZM31.0593 9.67471C30.9073 9.9049 30.7357 10.1613 30.5483 10.432C29.8496 11.4415 28.9031 12.7082 27.9883 13.5905C27.0735 14.4727 25.7771 15.3694 24.7465 16.0275C24.2242 16.3611 23.7563 16.6423 23.4188 16.8402C23.3805 16.8627 23.3433 16.8832 23.3084 16.9035C23.3247 17.0866 23.3333 17.2719 23.3333 17.4593C23.3333 18.464 23.0934 19.4119 22.669 20.2484C22.9989 20.3488 23.412 20.4714 23.8714 20.5952C25.0287 20.9071 26.4173 21.2197 27.4923 21.2874C28.5673 21.355 29.9831 21.2184 31.1697 21.054C31.7546 20.973 32.2688 20.8872 32.6363 20.8219C32.7387 20.8038 32.8294 20.7863 32.9072 20.7719C33.1018 19.7736 33.2051 18.742 33.2051 17.686C33.2051 14.7637 32.4226 12.0264 31.0593 9.67471ZM21.9692 2.61199C21.9714 2.98466 21.9685 3.50448 21.9486 4.0938C21.9095 5.25426 21.8026 6.75349 21.5117 7.9294C21.2208 9.10531 20.617 10.4789 20.1112 11.5214C20.0405 11.6673 19.9707 11.8077 19.9035 11.9417C21.2246 12.5855 22.2792 13.6997 22.8544 15.0692C23.1249 14.9069 23.4445 14.7128 23.7873 14.4939C24.7999 13.8472 25.9703 13.0298 26.7495 12.2783C27.5287 11.5268 28.3912 10.384 29.0777 9.39217C29.4161 8.90324 29.7029 8.46338 29.905 8.14641C29.9286 8.10939 29.9504 8.07362 29.9716 8.04012C27.9777 5.41094 25.1889 3.43062 21.9675 2.46629C21.9679 2.51197 21.9689 2.5606 21.9692 2.61199ZM7.83854 5.17172C5.32653 7.15534 3.42754 9.89481 2.4754 13.0515C2.71678 13.0258 2.98368 13.0001 3.26723 12.9762C4.41219 12.8799 5.8979 12.812 7.08746 12.9665C8.27705 13.121 9.69741 13.5665 10.7811 13.9523C11.2616 14.1234 11.6881 14.2881 12.0168 14.4186C12.4243 13.7003 12.9717 13.0733 13.6215 12.5759C13.5045 12.3068 13.365 11.991 13.2061 11.6521C12.7236 10.623 12.1022 9.43548 11.4945 8.63311C10.8867 7.83079 9.91505 6.91495 9.05806 6.17568C8.63657 5.8121 8.25532 5.50161 7.97964 5.28243C7.92907 5.24223 7.88151 5.20548 7.83854 5.17172ZM17.5 1.81395C14.5298 1.81395 11.7523 2.64764 9.38364 4.09557C9.62724 4.29341 9.91508 4.53029 10.2228 4.79573C11.0967 5.54957 12.1904 6.56835 12.9195 7.53083C13.6485 8.49333 14.3359 9.82597 14.8278 10.8753C14.9679 11.1741 15.0938 11.4553 15.2034 11.7048C15.8499 11.4669 16.5478 11.3372 17.2756 11.3372C17.5763 11.3372 17.8719 11.3596 18.1608 11.4023C18.2665 11.1958 18.3815 10.9671 18.5 10.723C18.996 9.70047 19.5278 8.46951 19.7703 7.4892C20.0128 6.50894 20.1167 5.1703 20.1551 4.03224C20.1739 3.47245 20.1768 2.97755 20.1748 2.62306C20.1737 2.44602 20.1715 2.30421 20.1695 2.20766C20.1686 2.15941 20.1675 2.12234 20.1669 2.09783C20.1666 2.08573 20.1662 2.07668 20.166 2.07081V2.06284L20.2659 2.0593C19.368 1.89806 18.4437 1.81395 17.5 1.81395Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },

  {
    key: 'laundry_service_available',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M19.1773 0C20.6365 7.29268e-05 21.801 0.397431 22.6704 1.19238C23.5398 1.98736 23.9732 2.94762 23.9732 4.07422C23.9732 4.72621 23.8202 5.32702 23.5152 5.87598C23.21 6.42606 22.6121 7.09494 21.7232 7.88281C21.1261 8.41249 20.735 8.80363 20.5513 9.05469C20.3677 9.30569 20.2318 9.59447 20.1441 9.91992C20.1126 10.0363 20.0869 10.1791 20.065 10.3477L36.732 17.8369C37.678 18.2621 38.185 19.3024 37.937 20.3096L36.8345 24.7891L36.7827 24.9658C36.5092 25.7729 35.7813 26.3436 34.9322 26.416L34.7486 26.4238H31.2759V35.6602C31.2759 37.7957 29.5452 39.5273 27.4097 39.5273H12.3726C10.2371 39.5272 8.5054 37.7956 8.5054 35.6602V26.4238H4.51712L4.34036 26.417C3.52516 26.3499 2.81713 25.8229 2.51809 25.0615L2.46048 24.8945L1.09134 20.3447C0.803949 19.3891 1.21325 18.3709 2.05911 17.874L2.23392 17.7822L18.1382 10.3027C18.1764 10.2848 18.2165 10.2704 18.2554 10.2549C18.3013 9.82582 18.3914 9.43941 18.5249 9.0957C18.6739 8.70176 18.9159 8.30441 19.2486 7.90332C19.4935 7.61147 19.9323 7.18509 20.5669 6.62598C21.2017 6.06568 21.6144 5.61884 21.8052 5.28613C21.9959 4.95356 22.0894 4.59004 22.0894 4.19629C22.0893 3.48303 21.8106 2.85719 21.2544 2.31738C20.6983 1.77773 20.0157 1.5088 19.2075 1.50879C18.4269 1.50879 17.775 1.75347 17.2525 2.24219C16.8708 2.59939 16.5839 3.10376 16.3941 3.75488C16.2634 4.20183 15.8271 4.48772 15.3648 4.43262C14.7791 4.36309 14.3937 3.78314 14.5611 3.2168C14.8183 2.3463 15.2489 1.64502 15.8521 1.1123C16.6899 0.370459 17.7986 0 19.1773 0ZM10.2241 26.4238V35.6602C10.2241 36.8465 11.1863 37.8085 12.3726 37.8086H27.4097C28.596 37.8085 29.5581 36.8465 29.5581 35.6602V26.4238H10.2241ZM2.96536 19.3369C2.77313 19.4276 2.67572 19.646 2.73684 19.8496L4.10599 24.3994L4.13138 24.4648C4.20257 24.6099 4.35153 24.7049 4.51712 24.7051H8.5054V21.4824H10.2241V24.7051H29.5581V21.4824H31.2759V24.7051H34.7486C34.946 24.705 35.1181 24.5696 35.1656 24.3779L36.2681 19.8984C36.3113 19.7223 36.2397 19.5409 36.0943 19.4414L36.0269 19.4043L26.7202 15.2217C25.8354 17.9953 22.811 19.7627 19.5777 19.7627C16.2342 19.7625 13.2751 17.7383 12.4829 14.8604L2.96536 19.3369ZM18.9566 11.8281L18.8687 11.8584L14.0747 14.1123C14.5155 16.27 16.7181 18.0437 19.5777 18.0439C22.4016 18.0439 24.5862 16.5021 25.1294 14.5068L19.2281 11.8555C19.1422 11.8169 19.0467 11.8076 18.9566 11.8281Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },

  {
    key: 'access_to_washing_area',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M39 20.8633L38.9961 21.1973C38.8215 28.0862 33.2688 33.6389 26.3799 33.8135L26.0459 33.8174H25.1826V36.4082L25.1709 36.6289C25.0677 37.6448 24.2599 38.4522 23.2441 38.5557L23.0234 38.5674H16.5459L16.3252 38.5557C15.309 38.4525 14.5007 37.6451 14.3975 36.6289L14.3867 36.4082V33.8174H13.9541L13.6201 33.8135C6.7312 33.6389 1.17846 28.0862 1.00391 21.1973L1 20.8633V19.1357H39V20.8633ZM16.1143 36.4082C16.1144 36.6466 16.3075 36.8398 16.5459 36.8398H23.0234C23.2616 36.8395 23.455 36.6464 23.4551 36.4082V33.8174H16.1143V36.4082ZM2.72754 20.8633C2.72777 27.0636 7.7538 32.0896 13.9541 32.0898H26.0459C32.0526 32.0896 36.9572 27.3725 37.2578 21.4404L37.2725 20.8633H2.72754ZM18.7051 1C22.5207 1.00024 25.6133 4.09354 25.6133 7.90918V8.77148H25.6143V10.0674C25.6141 11.2596 24.6473 12.2264 23.4551 12.2266H22.5918C21.3994 12.2266 20.4327 11.2597 20.4326 10.0674V5.31738H22.1592V10.0674C22.1593 10.3058 22.3534 10.499 22.5918 10.499H23.4551C23.6933 10.4988 23.8866 10.3056 23.8867 10.0674V7.90918C23.8867 5.04749 21.5667 2.7268 18.7051 2.72656C15.8439 2.72665 13.5234 5.04622 13.5225 7.90723V19.1348H11.7949V7.90723H11.7959C11.7968 4.09227 14.8899 1.00009 18.7051 1ZM24.3174 13.9541C24.7943 13.9541 25.1806 14.3404 25.1807 14.8174V16.5449L25.1768 16.6328C25.1326 17.0684 24.7646 17.4082 24.3174 17.4082C23.8703 17.408 23.5021 17.0683 23.458 16.6328L23.4541 16.5449V14.8174C23.4541 14.3406 23.8406 13.9543 24.3174 13.9541ZM8.34082 13.5215C8.81779 13.5215 9.2041 13.9078 9.2041 14.3848V16.1123L9.2002 16.2002C9.15592 16.6356 8.78794 16.9756 8.34082 16.9756C7.89384 16.9754 7.52568 16.6355 7.48145 16.2002L7.47754 16.1123V14.3848C7.47754 13.9079 7.86402 13.5217 8.34082 13.5215ZM16.9775 13.0898C17.4544 13.0899 17.8406 13.4763 17.8408 13.9531V15.6807L17.8369 15.7686C17.7928 16.2041 17.4247 16.5439 16.9775 16.5439C16.5303 16.5439 16.1623 16.2041 16.1182 15.7686L16.1143 15.6807V13.9531C16.1144 13.4763 16.5007 13.0898 16.9775 13.0898Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },
  {
    key: 'soap_washing_provided',
    icon: (
      <svg
        width="41"
        height="40"
        viewBox="0 0 41 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M25.6455 0C27.1195 4.32946e-05 29.1973 0.317725 31.2393 0.719727C33.2995 1.12533 35.3908 1.63078 36.9229 2.0332C37.9231 2.29606 38.6005 3.20206 38.6006 4.21875V5.35742C38.6006 5.4194 38.5991 5.52491 38.5791 5.64844L38.5518 5.77832C38.4143 6.30717 38.0027 6.88068 37.2412 7.11621C36.5166 7.34006 35.6295 7.20974 34.5879 6.73633C34.2243 6.57107 33.522 6.41239 32.5225 6.28711C31.625 6.17464 30.5647 6.09945 29.4482 6.0498L28.5752 8.57812C31.8826 10.0779 34.1845 13.4066 34.1846 17.2744V33.1836C34.1843 36.1957 31.7417 38.6375 28.7295 38.6377H16.877C15.9156 39.4849 14.6545 40 13.2725 40H6.45508C3.53685 39.9999 1.15288 37.7081 1.00684 34.8262L1 34.5459V25.9092H1.04785C1.13824 25.1657 1.29301 24.1539 1.44824 23.1768C1.63365 22.0097 1.81737 20.8869 1.90332 20.2295C1.97142 19.7082 1.87003 18.6541 1.6709 17.4316C1.52688 16.5477 1.34509 15.6486 1.1875 14.9404L1.04004 14.3037L1.02246 14.2119C1.01693 14.1711 1.01562 14.1301 1.01562 14.0898H1C1 10.6197 3.04785 7.62885 6 6.25586V4.41406C6 4.0614 6.08259 3.71292 6.24023 3.39746L6.85547 2.16699L6.93262 2.02539C7.33943 1.33776 8.08184 0.910254 8.88867 0.910156H11.126C12.1263 0.910156 13.0095 1.56452 13.3008 2.52148L14.5732 6.70508C16.241 7.71955 17.5368 9.28391 18.2119 11.1455C19.3757 9.61388 21.0389 8.48311 22.959 7.99512L22.9355 7.94434C22.6505 7.35772 22.2767 6.62339 21.9043 5.96387L21.6816 5.5791C21.5062 5.28341 21.345 5.03039 21.2051 4.83496L21.0723 4.65918C20.3206 3.71956 20.374 2.42359 21.2529 1.46094C22.0916 0.542755 23.5847 2.07646e-05 25.6455 0ZM9.63672 7.27148C5.92116 7.27163 2.90071 10.2439 2.82129 13.9404L2.96094 14.543C3.12523 15.2811 3.31433 16.2158 3.46484 17.1396C3.65829 18.3272 3.81405 19.6397 3.70605 20.4658C3.61579 21.1561 3.42562 22.3195 3.24414 23.4619C3.06307 24.6017 2.89127 25.7194 2.81836 26.4141V34.5459L2.82324 34.7324C2.92051 36.6538 4.50958 38.1815 6.45508 38.1816H13.2725C15.2807 38.1816 16.909 36.554 16.9092 34.5459V26.2832C16.8297 25.5839 16.6691 24.5326 16.499 23.4619C16.3175 22.3195 16.1274 21.1561 16.0371 20.4658C15.9291 19.6397 16.0849 18.3272 16.2783 17.1396C16.3349 16.7921 16.3978 16.443 16.4619 16.1035C16.4874 15.2483 16.6368 14.4248 16.8936 13.6494C16.6666 10.0891 13.7084 7.27148 10.0908 7.27148H9.63672ZM18.7275 34.5459C18.7275 35.3575 18.5485 36.1271 18.2305 36.8193H28.7295C30.4242 36.8192 31.8477 35.6598 32.251 34.0908H18.7275V34.5459ZM32.3652 18.6377H17.9033C17.8241 19.3266 17.7956 19.8906 17.8398 20.2295C17.9258 20.8869 18.1095 22.0096 18.2949 23.1768C18.4502 24.1539 18.6049 25.1657 18.6953 25.9092H18.7275V26.1807C18.7309 26.2115 18.7353 26.2419 18.7383 26.2715C18.7471 26.3581 18.7422 26.4428 18.7275 26.5244V32.2725H32.3652V33.2207C32.3654 33.2084 32.3662 33.1959 32.3662 33.1836V17.2744C32.3662 17.2624 32.3653 17.2503 32.3652 17.2383V18.6377ZM7.81836 25C7.81836 25.2508 8.02171 25.4538 8.27246 25.4541H11.4551C11.7059 25.4539 11.9092 25.2509 11.9092 25V16.8184H9.6377C9.13568 16.8183 8.72852 16.4112 8.72852 15.9092C8.72866 15.4073 9.13577 15.0001 9.6377 15H13.7275V25C13.7275 26.255 12.7101 27.2722 11.4551 27.2725H8.27246C7.01756 27.2721 6 26.255 6 25V15H7.81836V25ZM24.8672 9.55176C22.0574 9.64302 19.6777 11.4335 18.7227 13.9297C18.7232 13.9616 18.7254 13.9934 18.7256 14.0254C18.73 14.0866 18.7292 14.1492 18.7207 14.2119L18.7031 14.3037L18.5557 14.9404C18.4663 15.3419 18.369 15.8045 18.2754 16.291C18.2751 16.3157 18.2744 16.3405 18.2744 16.3652V16.8193H32.3516C32.1203 12.839 28.8772 9.6684 24.8672 9.55176ZM38.0508 10.9229C39.1969 11.0624 40.0917 12.2246 40.0918 13.6367L40.0801 13.915C39.9638 15.2904 38.9952 16.3633 37.8184 16.3633L37.5859 16.3496C36.5166 16.2189 35.6661 15.1984 35.5576 13.915L35.5459 13.6367C35.546 12.1309 36.5635 10.9096 37.8184 10.9092L38.0508 10.9229ZM37.8184 12.7275C37.8085 12.7276 37.7271 12.7296 37.6084 12.8721C37.4833 13.0222 37.3643 13.287 37.3643 13.6367C37.3643 13.9863 37.4834 14.2512 37.6084 14.4014C37.727 14.5435 37.8085 14.5459 37.8184 14.5459C37.8245 14.5459 37.9074 14.5476 38.0293 14.4014C38.1544 14.2512 38.2734 13.9865 38.2734 13.6367C38.2734 13.2871 38.1543 13.0222 38.0293 12.8721C37.9076 12.7261 37.8245 12.7275 37.8184 12.7275ZM23.9814 5.9873C24.3303 6.65409 24.6438 7.29554 24.8477 7.7334C24.8542 7.73354 24.8607 7.73422 24.8672 7.73438C24.9421 7.73246 25.0174 7.72852 25.0928 7.72852V7.74023C25.6965 7.76853 26.2852 7.85387 26.8545 7.98926L27.5449 5.99023C26.2858 5.96635 25.0472 5.96946 23.9814 5.9873ZM8.88867 2.72852C8.73817 2.72861 8.59867 2.80315 8.51465 2.9248L8.48145 2.97949L7.86621 4.21094C7.83487 4.27388 7.81836 4.34374 7.81836 4.41406V5.64648C8.40472 5.52078 9.01283 5.45314 9.63672 5.45312H10.0908C10.8855 5.45312 11.6549 5.56174 12.3857 5.7627L11.5605 3.05078C11.5023 2.85939 11.326 2.72852 11.126 2.72852H8.88867ZM25.6455 1.81836C23.8442 1.81838 22.9507 2.2983 22.5957 2.68652C22.2815 3.03067 22.3348 3.3267 22.4922 3.52344L22.6777 3.76953C22.7721 3.90092 22.8667 4.04372 22.9619 4.19238C24.6405 4.14652 27.0111 4.12942 29.25 4.21973C30.4923 4.26985 31.7087 4.35302 32.749 4.4834C33.7596 4.61007 34.7036 4.79281 35.3398 5.08203C36.1819 5.46476 36.5809 5.41729 36.7051 5.37891C36.75 5.36489 36.7718 5.34858 36.7822 5.33691V4.21875C36.7821 4.01072 36.6452 3.8405 36.4609 3.79199C34.955 3.39642 32.9015 2.90038 30.8877 2.50391C28.8554 2.10381 26.9282 1.8184 25.6455 1.81836Z"
          fill="#FE7755"
        />
      </svg>
    ),
  },
];

export const hostLanguage = [
  {
    key: 'fluent-english',
    label: 'Fluent English',
  },
  {
    key: 'basic-english',
    label: 'Basic English',
  },
  {
    key: 'itaukei-dialect',
    label: 'Itaukei Dialect',
  },
  {
    key: 'fiji-hindi',
    label: 'Fiji Hindi',
  },
];

export const sleepingOptions = [
  { value: 'private_room', label: 'Private Room' },
  { value: 'shared_room', label: 'Shared Room' },
];

export const listArray = [
  {
    attributes: {
      title: 'Listing in Ba',
      publicData: {
        province_filter: 'ba-province-filter',
        location: { address: 'Not far from Nadi, Fiji', building: '1' },
        categoryLevel1: 'customaryland_homestay',
        categoryLevel2: 'cutomaryland_corrugated',
        categoryLevel3: 'customaryland_corrugated_standard_outdoor',
        featured_listing: true,
      },
      geolocation: { lat: -17.7504, lng: 177.4536 },
      price: { amount: 50000, currency: 'USD' },
      state: 'published',
    },
    id: { uuid: '11111111-1111-1111-1111-111111111111' },
    type: 'listing',
    images: nandi,
  },

  {
    attributes: {
      title: 'Listing in Ba',
      publicData: {
        province_filter: 'ba-province-filter',
        location: { address: 'Down the Coral Coast, Fiji', building: '1' },
        categoryLevel1: 'customaryland_homestay',
        categoryLevel2: 'cutomaryland_corrugated',
        categoryLevel3: 'customaryland_corrugated_standard_outdoor',
        featured_listing: true,
      },
      geolocation: { lat: -18.18, lng: 177.85 },
      price: { amount: 50000, currency: 'USD' },
      state: 'published',
    },
    id: { uuid: '11111111-1111-1111-1111-111111111111' },
    type: 'listing',
    images: coral,
  },

  {
    attributes: {
      title: 'Listing in Ba',
      publicData: {
        province_filter: 'ba-province-filter',
        location: { address: 'Along the Sun Coast, Fiji', building: '1' },
        categoryLevel1: 'customaryland_homestay',
        categoryLevel2: 'cutomaryland_corrugated',
        categoryLevel3: 'customaryland_corrugated_standard_outdoor',
        featured_listing: true,
      },
      geolocation: { lat: -17.49, lng: 177.78 },
      price: { amount: 50000, currency: 'USD' },
      state: 'published',
    },
    id: { uuid: '11111111-1111-1111-1111-111111111111' },
    type: 'listing',
    images: sunCoast,
  },

  {
    attributes: {
      title: 'Listing in Ba',
      publicData: {
        province_filter: 'ba-province-filter',
        location: { address: 'Close to Pacific Harbour, Fiji', building: '1' },
        categoryLevel1: 'customaryland_homestay',
        categoryLevel2: 'cutomaryland_corrugated',
        categoryLevel3: 'customaryland_corrugated_standard_outdoor',
        featured_listing: true,
      },
      geolocation: { lat: -18.245, lng: 178.065 },
      price: { amount: 50000, currency: 'USD' },
      state: 'published',
    },
    id: { uuid: '11111111-1111-1111-1111-111111111111' },
    type: 'listing',
    images: pacific,
  },
  {
    attributes: {
      title: 'Listing in Ba',
      publicData: {
        province_filter: 'ba-province-filter',
        location: { address: 'Over in Savusavu, Fiji', building: '1' },
        categoryLevel1: 'customaryland_homestay',
        categoryLevel2: 'cutomaryland_corrugated',
        categoryLevel3: 'customaryland_corrugated_standard_outdoor',
        featured_listing: true,
      },
      geolocation: { lat: -16.8, lng: 179.33 },
      price: { amount: 50000, currency: 'USD' },
      state: 'published',
    },
    id: { uuid: '11111111-1111-1111-1111-111111111111' },
    type: 'listing',
    images: savusavu,
  },
  {
    attributes: {
      title: 'Listing in Ba',
      publicData: {
        province_filter: 'ba-province-filter',
        location: { address: 'Up in Taveuni, Fiji', building: '1' },
        categoryLevel1: 'customaryland_homestay',
        categoryLevel2: 'cutomaryland_corrugated',
        categoryLevel3: 'customaryland_corrugated_standard_outdoor',
        featured_listing: true,
      },
      geolocation: { lat: -16.85, lng: 179.9667 },
      price: { amount: 50000, currency: 'USD' },
      state: 'published',
    },
    id: { uuid: '11111111-1111-1111-1111-111111111111' },
    type: 'listing',
    images: taveuni,
  },
  {
    attributes: {
      title: 'Listing in Ba',
      publicData: {
        province_filter: 'ba-province-filter',
        location: { address: 'Down in Kadavu, Fiji', building: '1' },
        categoryLevel1: 'customaryland_homestay',
        categoryLevel2: 'cutomaryland_corrugated',
        categoryLevel3: 'customaryland_corrugated_standard_outdoor',
        featured_listing: true,
      },
      geolocation: { lat: -19.05, lng: 178.15 },
      price: { amount: 50000, currency: 'USD' },
      state: 'published',
    },
    id: { uuid: '11111111-1111-1111-1111-111111111111' },
    type: 'listing',
    images: kadavu,
  },

  {
    attributes: {
      title: 'Listing in Ba',
      publicData: {
        province_filter: 'ba-province-filter',
        location: { address: 'Far out in Lau, Fiji', building: '1' },
        categoryLevel1: 'customaryland_homestay',
        categoryLevel2: 'cutomaryland_corrugated',
        categoryLevel3: 'customaryland_corrugated_standard_outdoor',
        featured_listing: true,
      },
      geolocation: { lat: -19.18, lng: -178.8 },
      price: { amount: 50000, currency: 'USD' },
      state: 'published',
    },
    id: { uuid: '11111111-1111-1111-1111-111111111111' },
    type: 'listing',
    images: lau,
  },
  {
    attributes: {
      title: 'Listing in Ba',
      publicData: {
        province_filter: 'ba-province-filter',
        location: { address: 'Way up in Rotuma, Fiji', building: '1' },
        categoryLevel1: 'customaryland_homestay',
        categoryLevel2: 'cutomaryland_corrugated',
        categoryLevel3: 'customaryland_corrugated_standard_outdoor',
        featured_listing: true,
      },
      geolocation: { lat: -12.5, lng: 177.07 },
      price: { amount: 50000, currency: 'USD' },
      state: 'published',
    },
    id: { uuid: '11111111-1111-1111-1111-111111111111' },
    type: 'listing',
    images: rotu,
  },
  {
    attributes: {
      title: 'Listing in Ba',
      publicData: {
        province_filter: 'ba-province-filter',
        location: { address: 'Out in the Yasawa’s, Fiji', building: '1' },
        categoryLevel1: 'customaryland_homestay',
        categoryLevel2: 'cutomaryland_corrugated',
        categoryLevel3: 'customaryland_corrugated_standard_outdoor',
        featured_listing: true,
      },
      geolocation: { lat: -17.0, lng: 177.25 },
      price: { amount: 50000, currency: 'USD' },
      state: 'published',
    },
    id: { uuid: '11111111-1111-1111-1111-111111111111' },
    type: 'listing',
    images: Yasawa,
  },
  {
    attributes: {
      title: 'Listing in Ba',
      publicData: {
        province_filter: 'ba-province-filter',
        location: { address: 'Across the Mamanuca’s, Fiji', building: '1' },
        categoryLevel1: 'customaryland_homestay',
        categoryLevel2: 'cutomaryland_corrugated',
        categoryLevel3: 'customaryland_corrugated_standard_outdoor',
        featured_listing: true,
      },
      geolocation: { lat: -17.75, lng: 177.15 },
      price: { amount: 50000, currency: 'USD' },
      state: 'published',
    },
    id: { uuid: '11111111-1111-1111-1111-111111111111' },
    type: 'listing',
    images: Mamanuca,
  },
  {
    attributes: {
      title: 'Listing in Ba',
      publicData: {
        province_filter: 'ba-province-filter',
        location: { address: 'Up in the Highlands, Fiji', building: '1' },
        categoryLevel1: 'customaryland_homestay',
        categoryLevel2: 'cutomaryland_corrugated',
        categoryLevel3: 'customaryland_corrugated_standard_outdoor',
        featured_listing: true,
      },
      geolocation: { lat: -17.75, lng: 177.58 },
      price: { amount: 50000, currency: 'USD' },
      state: 'published',
    },
    id: { uuid: '11111111-1111-1111-1111-111111111111' },
    type: 'listing',
    images: HighlandsImage,
  },
  {
    attributes: {
      title: 'Listing in Ba',
      publicData: {
        province_filter: 'ba-province-filter',
        location: { address: 'Over in Ovalau, Fiji', building: '1' },
        categoryLevel1: 'customaryland_homestay',
        categoryLevel2: 'cutomaryland_corrugated',
        categoryLevel3: 'customaryland_corrugated_standard_outdoor',
        featured_listing: true,
      },
      geolocation: { lat: -17.6833, lng: 178.8 },
      price: { amount: 50000, currency: 'USD' },
      state: 'published',
    },
    id: { uuid: '11111111-1111-1111-1111-111111111111' },
    type: 'listing',
    images: OvalauImage,
  },
  // {
  //   attributes: {
  //     title: "Listing in Ba",
  //     publicData: {
  //       province_filter: "ba-province-filter",
  //       location: { address: "Around Labasa, Fiji", building: "1" },
  //       categoryLevel1: "customaryland_homestay",
  //       categoryLevel2: "cutomaryland_corrugated",
  //       categoryLevel3: "customaryland_corrugated_standard_outdoor",
  //       featured_listing: true
  //     },
  //     geolocation: { lat: -16.4333, lng: 179.3833 },
  //     price: { amount: 50000, currency: "USD" },
  //     state: "published"
  //   },
  //   id: { uuid: "11111111-1111-1111-1111-111111111111" },
  //   type: "listing",
  //   images:nandi
  // },
  // {
  //   attributes: {
  //     title: "Listing in Ba",
  //     publicData: {
  //       province_filter: "ba-province-filter",
  //       location: { address: "Outside Suva, Fiji", building: "1" },
  //       categoryLevel1: "customaryland_homestay",
  //       categoryLevel2: "cutomaryland_corrugated",
  //       categoryLevel3: "customaryland_corrugated_standard_outdoor",
  //       featured_listing: true
  //     },
  //     geolocation: { lat: -18.1200, lng: 178.4500 },
  //     price: { amount: 50000, currency: "USD" },
  //     state: "published"
  //   },
  //   id: { uuid: "11111111-1111-1111-1111-111111111111" },
  //   type: "listing",
  //   images:nandi
  // },
  // {
  //   attributes: {
  //     title: "Listing in Ba",
  //     publicData: {
  //       province_filter: "ba-province-filter",
  //       location: { address: "Near Nausori, Fiji", building: "1" },
  //       categoryLevel1: "customaryland_homestay",
  //       categoryLevel2: "cutomaryland_corrugated",
  //       categoryLevel3: "customaryland_corrugated_standard_outdoor",
  //       featured_listing: true
  //     },
  //     geolocation: { lat: -18.0300, lng: 178.5600 },
  //     price: { amount: 50000, currency: "USD" },
  //     state: "published"
  //   },
  //   id: { uuid: "11111111-1111-1111-1111-111111111111" },
  //   type: "listing",
  //   images:nandi
  // },
  // {
  //   attributes: {
  //     title: "Listing in Ba",
  //     publicData: {
  //       province_filter: "ba-province-filter",
  //       location: { address: "Up in Qamea, Fiji", building: "1" },
  //       categoryLevel1: "customaryland_homestay",
  //       categoryLevel2: "cutomaryland_corrugated",
  //       categoryLevel3: "customaryland_corrugated_standard_outdoor",
  //       featured_listing: true
  //     },
  //     geolocation: { lat: -16.7333, lng: 179.8500 },
  //     price: { amount: 50000, currency: "USD" },
  //     state: "published"
  //   },
  //   id: { uuid: "11111111-1111-1111-1111-111111111111" },
  //   type: "listing",
  //   images:nandi
  // }
];

export const hardcodedPopularDestinations = [
  {
    label: 'Along the Sun Coast',
    lat: -17.8,
    lng: 177.4,
    bounds: {
      ne: { lat: -17.75, lng: 177.45 },
      sw: { lat: -17.85, lng: 177.35 },
    },
  },
  {
    label: 'Not far from Nadi',
    lat: -17.7544,
    lng: 177.435,
    bounds: {
      ne: { lat: -17.7044, lng: 177.485 },
      sw: { lat: -17.8044, lng: 177.385 },
    },
  },
  {
    label: 'Down the Coral Coast',
    lat: -18.2167,
    lng: 177.6333,
    bounds: {
      ne: { lat: -18.1667, lng: 177.6833 },
      sw: { lat: -18.2667, lng: 177.5833 },
    },
  },
  {
    label: 'Close to Pacific Harbour',
    lat: -18.2475,
    lng: 178.0708,
    bounds: {
      ne: { lat: -18.1975, lng: 178.1208 },
      sw: { lat: -18.2975, lng: 178.0208 },
    },
  },
  {
    label: 'Over in Savusavu',
    lat: -16.8027,
    lng: 179.3317,
    bounds: {
      ne: { lat: -16.7527, lng: 179.3817 },
      sw: { lat: -16.8527, lng: 179.2817 },
    },
  },
];

export const infoContentListing = [
  {
    title: 'This listing is managed by fijianhost.com',
    floatContent: `This listing is managed by fijianhost.com to support rural hosts who may have limited
internet, devices or digital skills. When you message the host, our support team will
respond and coordinate with them on your behalf. Response times may vary depending on
how quickly the host is able to reply to our customer support team.`,
  },
];
